<template>
    <div class="ai-modal" ref="modal" v-show="is_visible" @mouseleave="hideAnnotation" data-tt-assistant-root="1">
        <span @click="hideModal" class="ai-modal-close">×</span>
        <div
            class="ai-modal-resize-handle"
            @mousedown="startResizing"
        ></div>
        <h2 class="ai-modal-title">
            <i class="tticon tticon-stars_black_24dp ai-title-icon" aria-hidden="true"></i>
            <span>{{ labels.title }}</span>
        </h2>
        <div class="annotation-overlay" ref="annotation_overlay" @mouseleave="hideAnnotation"></div>
        <div class="ai-modal-body" :class="{ 'has-inflight-scroll-slack': is_request_in_flight }" ref="ai-body" v-if="!is_community_edition" @click="onConversationClick">
            <div
                class="conversation-history"
                :class="getConversationRowClass( item )"
                v-for="item in completions"
                :data-message-id="item.message_id || ''"
                @mouseover="showAnnotation"
                @mouseleave="hideAnnotation"
            >
                <div v-if="item.type === 'choice'" :id="'ai-msg-'+item.id" class="conversation-message choice-message">
                    <p v-html="item.result"></p>
                    <ul class="ai-choice-list">
                        <li class="ai-choice" v-for="choice in item.choices" @click="selectedChoice(choice)">{{ choice.message }}</li>
                    </ul>
                </div>
                <div
                    v-else-if="item.role === 'assistant'"
                    :id="'ai-msg-'+item.id"
                    class="conversation-message assistant-message"
                >
                    <span class="assistant-inline-spinner-slot">
                        <ProgressSpinner
                            v-if="!item.id && show_spinner"
                            class="assistant-inline-spinner"
                            style="width:16px;height:16px"
                            strokeWidth="5"
                            animationDuration="2s"
                        />
                    </span>
                    <div class="assistant-inline-message-body">
                        <span class="assistant-inline-message-content" v-html="item.result"></span>
                        <div v-if="item.id" class="assistant-feedback-controls">
                            <button
                                type="button"
                                class="assistant-feedback-button"
                                :class="{ 'is-selected': item.user_feedback_rating === 'positive' }"
                                :title="labels.feedback_positive"
                                :aria-label="labels.feedback_positive"
                                :disabled="item.feedback_in_progress === true"
                                @click.stop="onAssistantFeedbackClick( item, 'positive' )"
                            >
                                <i class="tticon tticon-thumb_up_black_24dp" aria-hidden="true"></i>
                            </button>
                            <button
                                type="button"
                                class="assistant-feedback-button"
                                :class="{ 'is-selected': item.user_feedback_rating === 'negative' }"
                                :title="labels.feedback_negative"
                                :aria-label="labels.feedback_negative"
                                :disabled="item.feedback_in_progress === true"
                                @click.stop="onAssistantFeedbackClick( item, 'negative' )"
                            >
                                <i class="tticon tticon-thumb_down_black_24dp" aria-hidden="true"></i>
                            </button>
                        </div>
                    </div>
                </div>
                <div v-else-if="item.role === 'user'" :id="'ai-msg-'+item.id" class="conversation-message user-message" v-html="item.result"></div>
                <div v-else-if="item.role === 'system'" :id="'ai-msg-'+item.id" class="conversation-message system-message" v-html="item.result"></div>
            </div>
        </div>
        <div v-if="is_community_edition" class="ai-modal-body">
            <div class="conversation-history from-system">
                <div class="conversation-message system-message">
                    <p v-html="this.labels.upgrade_message"></p>
                </div>
            </div>
        </div>
        <div class="ai-modal-footer" v-if="!is_community_edition">
            <form @submit.prevent="userSubmitPrompt" class="form-container ai-form">
                <div v-if="is_browser_control_granted" class="browser-control-banner" role="status" aria-live="polite">
                    <span class="browser-control-banner-text">{{ labels.browser_control_active }}</span>
                    <button
                        type="button"
                        class="browser-control-revoke-button"
                        :title="labels.revoke_browser_control"
                        :aria-label="labels.revoke_browser_control"
                        @click="revokeBrowserControl"
                    >
                        {{ labels.revoke_browser_control }}
                    </button>
                </div>
                <div class="input-wrapper">
                    <textarea
                        class="ai-input ai-textarea"
                        v-model="user_prompt"
                        ref="user_prompt_input"
                        :disabled="input_disabled"
                        :placeholder="labels.prompt_placeholder"
                        rows="2"
                        @input="onPromptInput"
                        @keydown="onPromptKeyDown"
                    ></textarea>
                    <input
                        ref="attachment_input"
                        class="attachment-input"
                        type="file"
                        multiple
                        :disabled="input_disabled || is_processing_attachments"
                        @change="onAttachmentChange"
                    />
                    <div class="composer-actions">
                        <div class="composer-left-actions">
                            <button type="button" class="composer-icon-button attachment-button" :title="labels.attach_file" :aria-label="labels.attach_file" :disabled="input_disabled || is_processing_attachments" @click="openAttachmentPicker">
                                <i class="tticon tticon-file_upload_black_24dp"></i>
                            </button>
                            <button type="button" id="clear-button" class="composer-icon-button clear-button" :title="labels.new_chat" :aria-label="labels.new_chat" @click="clearContextWithConfirm">
                                <i class="tticon tticon-new_chat_black_24dp"></i>
                            </button>
                        </div>
                        <button
                            v-if="is_request_in_flight"
                            type="button"
                            class="composer-icon-button stop-button"
                            :title="labels.cancel"
                            :aria-label="labels.cancel"
                            @click="cancelTTAssistant"
                        >
                            <i class="tticon tticon-close_black_24dp"></i>
                        </button>
                        <button v-else type="submit" class="composer-icon-button send-button" :title="labels.send" :aria-label="labels.send" :disabled="!can_send_prompt">
                            <i class="tticon tticon-send_black_24dp"></i>
                        </button>
                    </div>
                </div>
                <div v-if="attached_files.length > 0" class="composer-attachment-list">
                    <span class="composer-attachment-chip" v-for="( attachment, index ) in attached_files" :key="attachment.id">
                        <span class="composer-attachment-name">{{ attachment.filename }}</span>
                        <span class="composer-attachment-size">({{ formatAttachmentSize( attachment.size_bytes ) }})</span>
                        <button type="button" class="composer-attachment-remove" :title="labels.remove_attachment" :aria-label="labels.remove_attachment" @click="removeAttachment( index )">×</button>
                    </span>
                </div>
            </form>
        </div>
        <div class="ai-disclaimer" v-if="!is_community_edition">
            <div class="ai-disclaimer-content">
                <small>{{ labels.ai_disclaimer }}</small>
            </div>
            <small v-if="conversation_token_count > 0" class="ai-token-counter">{{ formatTokenCount( conversation_token_count ) }}</small>
        </div>
    </div>
</template>
<script>
import ProgressSpinner from 'primevue/progressspinner';
import { Global } from '@/global/Global';
import TTVueUtils from '@/services/TTVueUtils';
import DOMPurify from 'dompurify';
import { marked } from 'marked';

export default {
    created() {
        this.event_bus = new TTEventBus( {
            component_id: this.component_id,
        } );
        this.event_bus.on( 'tt_assistant', 'show_tt_assistant', () => {
            this.showModal();
        } );
        this.event_bus.on( 'global', 'reset_vue_data', this.resetData );
        this.event_bus.on( 'global', 'finalize_tt_assistant_for_logout', this.finalizeConversationForLogout );

        this.createStartingMessage();
    },
    mounted() {
        document.querySelector( '.layout-content' ).style.marginRight = '480px';
        window.addEventListener( 'beforeunload', this.onWindowBeforeUnload );
        window.addEventListener( 'pagehide', this.onWindowPageHide );
        this.registerDebugHelpers();
        this.dispatchResizeEvent();
    },
    beforeUnmount() {
        window.removeEventListener( 'beforeunload', this.onWindowBeforeUnload );
        window.removeEventListener( 'pagehide', this.onWindowPageHide );
        this.clearHiddenConversationFinalizeTimer();
        this.unregisterDebugHelpers();
    },
    props: { // passed in via root props when component is mounted
        view_id: {
            type: String,
            default: null
        },
        component_id: { /* Note: This is passed in via TTVueUtils.mountComponent param, and auto added to root_props. */
            type: String,
            default: null
        },
    },
    data() {
        return {
            user_prompt: '',
            tt_assistant_api: TTAPI.APITTAssistant,
            user_dom_snapshot_nodes: {},
            user_dom_snapshot_counter: 0,
            user_dom_snapshot_mode: 'default',
            is_browser_control_granted: false,
            completions: [],
            show_spinner: false,
            response_id: null,
            ticket_response_id: null,
            conversation_session_id: null,
            conversation_started_epoch: null,
            conversation_ticket_submitted: false,
            // The feedback popup can be considered once per assistant conversation.
            // Store the conversation id after the close-trigger runs so repeated open/close
            // cycles on the same conversation do not repeatedly call TFeedback().
            feedback_prompt_conversation_session_id: null,
            hidden_conversation_finalize_timeout_id: null,
            hidden_conversation_finalize_delay_ms: 300000,
            last_user_prompt: '',
            current_workflow_goal: '',
            assistant_request_generation: 0,
            cancelled_request_generation: 0,
            input_disabled: false,
            attached_files: [],
            is_processing_attachments: false,
            max_attachment_count: 3,
            max_attachment_size_bytes: 5242880, //5MB, aligned with backend inline attachment validation.
            is_resizing: false,
            last_x: 0,
            is_visible: true,
            starting_prompt_choices: (() => {
                const prompts = [
                    { message: $.i18n._( 'How do I fix a missing punch?' ) },
                    { message: $.i18n._( 'How do I give an employee a $100 bonus?' ) },
                    { message: $.i18n._( 'How do I create an overtime policy?' ) },
                    { message: $.i18n._( 'How do I process payroll?' ) },
                    { message: $.i18n._( 'Can I change the date and time format?' ) },
                    { message: $.i18n._( 'Why is this employee showing overtime here?' ) },
                    { message: $.i18n._( 'Explain whats on my screen.' ) },
                    { message: $.i18n._( 'Explain all policies that affect '+ LocalCacheData.getLoginUser()?.first_name +' '+ LocalCacheData.getLoginUser()?.last_name +'.' ) },
                ];

                if ( LocalCacheData && LocalCacheData.getLoginUser() ) {
                    const country = LocalCacheData.getLoginUser()?.country;
                    const province = LocalCacheData.getLoginUser()?.province;
                    if ( country === 'US' && province != '' ) {
                        prompts.push( { message: $.i18n._( 'Explain overtime laws in ' + province + ', United States.' ) } );
                        prompts.push( { message: $.i18n._( 'Explain US federal overtime laws.' ) } );
                    } else if ( country === 'CA' && province != '' ) {
                        prompts.push( { message: $.i18n._( 'Explain overtime laws in ' + province + ', Canada.' ) } );
                        prompts.push( { message: $.i18n._( 'Explain vacation pay laws in ' + province + ', Canada.' ) } );
                    }
                }

                return prompts;
            })(),
            labels: {
                title: $.i18n._( 'AI Assistant' ) + ' (' + $.i18n._( 'Beta' ) + ')',
                new_chat: $.i18n._( 'New Chat' ),
                new_chat_confirm: $.i18n._( 'Start a new chat? This will clear the current conversation.' ),
                attach_file: $.i18n._( 'Attach File' ),
                remove_attachment: $.i18n._( 'Remove attachment' ),
                send: $.i18n._( 'Send' ),
                cancel: $.i18n._( 'Cancel' ),
                browser_control_active: $.i18n._( 'AI Assistant was granted control of your browser' ),
                revoke_browser_control: $.i18n._( 'REVOKE' ),
                reasoning_summary: $.i18n._( 'Reasoning' ),
                request_cancelled: $.i18n._( 'Request cancelled.' ),
                feedback_positive: $.i18n._( 'Helpful response' ),
                feedback_negative: $.i18n._( 'Not helpful response' ),
                feedback_submit_error: $.i18n._( 'Unable to save feedback. Please try again.' ),
                prompt_placeholder: $.i18n._( 'Ask a question about TimeTrex, payroll, or employment compliance...' ),
                ai_disclaimer: $.i18n._( 'Inaccurate information may be provided, double-check all responses.' ),
                upgrade_message: Global.getUpgradeMessage(),
            }
        };
    },
    computed: {
        is_community_edition() {
            return ( Global.getProductEdition() == 10 );
        },
        can_send_prompt() {
            let has_prompt = typeof this.user_prompt === 'string' && this.user_prompt.trim() !== '';
            let has_attachments = Array.isArray( this.attached_files ) && this.attached_files.length > 0;

            return this.input_disabled === false && this.is_processing_attachments === false && ( has_prompt || has_attachments );
        },
        is_request_in_flight() {
            return this.show_spinner === true;
        },
        conversation_token_count() {
            return this.getConversationTokenCount();
        }
    },
    methods: {
        resetData() {
            this.finalizeConversation( 'reset_vue_data' );
            Object.assign( this.$data, this.$options.data() );
            this.createStartingMessage();
            this.is_visible = false;
            document.querySelector( '.layout-content' ).style.marginRight = null;
            this.dispatchResizeEvent();
        },
        finalizeConversationForLogout() {
            this.finalizeConversation( 'logout', false, false );
        },
        registerDebugHelpers() {
            window.ttAssistantDebugSemanticSnapshot = ( active_scope_only = true, snapshot_mode = 'default' ) => {
                try {
                    let semantic_snapshot = this.buildUserDOMSnapshot( active_scope_only !== false, snapshot_mode );
                    let debug_summary = {
                        scope: semantic_snapshot?.scope || '',
                        snapshot_mode: semantic_snapshot?.snapshot_mode || '',
                        estimated_context_chars: semantic_snapshot?.estimated_context_chars || 0,
                        scope_root: semantic_snapshot?.scope_root || null,
                        actionable_elements_sent: Array.isArray( semantic_snapshot?.actionable_elements ) ? semantic_snapshot.actionable_elements.length : 0,
                        actionable_elements_total: semantic_snapshot?.actionable_element_count || 0,
                        actionable_elements_truncated: !!semantic_snapshot?.actionable_elements_truncated,
                        html_length: typeof semantic_snapshot?.html === 'string' ? semantic_snapshot.html.length : 0,
                        html_truncated: !!semantic_snapshot?.html_truncated,
                        active_element: semantic_snapshot?.active_element || null,
                        actionable_preview: Array.isArray( semantic_snapshot?.actionable_elements ) ? semantic_snapshot.actionable_elements.slice( 0, 20 ) : [],
                    };

                    window.__ttAssistantLastSemanticSnapshot = semantic_snapshot;
                    console.log( 'TT Assistant semantic snapshot summary', debug_summary );

                    return semantic_snapshot;
                } catch ( error ) {
                    console.error( 'TT Assistant semantic snapshot failed', error );
                    return {
                        error: true,
                        message: String( error?.message || error || 'Unknown snapshot error' )
                    };
                }
            };
        },
        unregisterDebugHelpers() {
            if ( window.ttAssistantDebugSemanticSnapshot ) {
                delete window.ttAssistantDebugSemanticSnapshot;
            }
        },
        startResizing( event ) {
            this.is_resizing = true;
            this.last_x = event.clientX;

            document.addEventListener( 'mousemove', this.onMouseMove );
            document.addEventListener( 'mouseup', this.onMouseUp );
        },
        onMouseMove( event ) {
            if ( !this.is_resizing ) {
                return;
            }

            const delta_x = event.clientX - this.last_x;
            const max_width = window.innerWidth * 0.5; // 50% of the window width
            const current_width = this.$refs.modal.getBoundingClientRect().width - delta_x;
            const new_width = Math.min( Math.max( 400, current_width ), max_width );

            this.$refs.modal.style.width = `${new_width}px`;
            this.last_x = event.clientX;
        },
        onMouseUp() {
            this.is_resizing = false;

            document.removeEventListener( 'mousemove', this.onMouseMove );
            document.removeEventListener( 'mouseup', this.onMouseUp );
        },
        scrollToBottom() {
            // Scroll right after Vue flushes DOM updates so large streamed responses stay pinned to the latest line
            // without the visible lag introduced by a fixed timeout.
            this.$nextTick( () => {
                const scroll_fn = () => {
                    const ai_body = this.$refs['ai-body'];
                    if ( ai_body ) {
                        ai_body.scrollTop = ai_body.scrollHeight;
                    }
                };

                if ( window.requestAnimationFrame ) {
                    window.requestAnimationFrame( scroll_fn );
                } else {
                    scroll_fn();
                }
            } );
        },
        scrollMessageToTop( message_id ) {
            if ( typeof message_id !== 'string' || message_id.trim() === '' ) {
                return;
            }

            this.$nextTick( () => {
                const scroll_fn = () => {
                    const ai_body = this.$refs['ai-body'];
                    if ( !ai_body ) {
                        return;
                    }

                    const escaped_message_id = ( window.CSS && typeof window.CSS.escape === 'function' )
                        ? window.CSS.escape( message_id )
                        : message_id.replace( /["\\]/g, '\\$&' );
                    const message_row = ai_body.querySelector( `.conversation-history[data-message-id="${escaped_message_id}"]` );
                    if ( message_row instanceof HTMLElement ) {
                        const preferred_anchor = message_row.querySelector( '.assistant-reasoning-summary-header, .assistant-reasoning-summary, .assistant-inline-message-body' );
                        const anchor_element = preferred_anchor instanceof HTMLElement ? preferred_anchor : message_row;
                        const ai_body_rect = ai_body.getBoundingClientRect();
                        const anchor_rect = anchor_element.getBoundingClientRect();
                        const anchor_top = ai_body.scrollTop + ( anchor_rect.top - ai_body_rect.top );

                        ai_body.scrollTop = Math.max( anchor_top - 8, 0 );
                    }
                };

                if ( window.requestAnimationFrame ) {
                    window.requestAnimationFrame( scroll_fn );
                } else {
                    scroll_fn();
                }
            } );
        },
        getConversationRowClass( item ) {
            if ( item?.type === 'choice' ) {
                // Starter choice prompt should visually match assistant bubbles, not centered system notices.
                return 'from-assistant';
            }

            if ( item?.role === 'user' ) {
                return 'from-user';
            }

            if ( item?.role === 'assistant' ) {
                return 'from-assistant';
            }

            return 'from-system';
        },
        clearContext( end_reason = null ) {
            if ( end_reason ) {
                this.finalizeConversation( end_reason );
            }

            this.cancelTTAssistant();
            this.resetConversationTracking();
            this.show_spinner = false;
            this.input_disabled = false;
            this.clearAttachments();
            this.completions = [];
            this.user_prompt = '';

            this.createStartingMessage();
            this.$nextTick( () => {
                this.resetPromptInputHeight();
            } );
        },
        clearContextWithConfirm() {
            // Skip confirmation when the chat only has the default starter message.
            if ( this.completions.length <= 1 || window.confirm( this.labels.new_chat_confirm ) ) {
                this.clearContext( 'new_chat' );
            }
        },
        createStartingMessage() {
            let login_user = LocalCacheData.getLoginUser();
            let first_name = '';
            if ( login_user && typeof login_user.first_name === 'string' ) {
                first_name = login_user.first_name.trim();
            }
            let greeting_name = first_name !== '' ? ( ' ' + first_name ) : '';

            let starting_choices = {
                role: 'system',
                type: 'choice',
                result: $.i18n._( `Hello${greeting_name},<br><br>I'm your AI assistant, ready to help with your questions. The more <a href="https://help.timetrex.com/latest/enterprise/Introduction/How-to-use-AI-Assistant.htm" target="_blank">detailed your questions</a>, the better assistance I can provide.<br><br>How can I help you today?` ),
                choices: this.getRandomStartingPromptChoices( 4 )
            };

            this.completions.push( starting_choices );
        },
        /*
         * Use an unbiased shuffle on a copy of the source prompts.
         * Why: sort(() => 0.5 - Math.random()) is biased and mutates the original
         * array, which makes the visible starter prompts feel less random over time.
         */
        getRandomStartingPromptChoices( limit = 4 ) {
            let choices = Array.isArray( this.starting_prompt_choices ) ? this.starting_prompt_choices.slice() : [];
            for ( let i = choices.length - 1; i > 0; i-- ) {
                let j = Math.floor( Math.random() * ( i + 1 ) );
                [ choices[i], choices[j] ] = [ choices[j], choices[i] ];
            }

            return choices.slice( 0, Math.max( 0, limit ) );
        },
        selectedChoice( choice ) {
            this.user_prompt = choice.message;
            this.userSubmitPrompt(null);
        },
        sendPrompt( user_prompt, user_input = {} ) {
            if ( this.input_disabled || this.is_processing_attachments ) {
                return;
            }

            let has_attachments = Array.isArray( this.attached_files ) && this.attached_files.length > 0;
            user_prompt = typeof user_prompt === 'string' ? user_prompt.trim() : '';
            if ( user_prompt === '' && has_attachments === false ) {
                return;
            }

            //When files are attached and no text is provided, still send a short instruction so AI analyzes attachments.
            if ( user_prompt === '' && has_attachments ) {
                user_prompt = $.i18n._( 'Please analyze the attached file(s).' );
            }

            this.last_user_prompt = user_prompt;
            this.updateWorkflowGoal( user_prompt );

            this.ensureConversationSession();

            let attached_files = this.attached_files.slice();
            this.addUserMessageToConversationHistory( user_prompt, attached_files );
            this.getCompletion( user_prompt, user_input );
            this.user_prompt = '';
            this.clearAttachments();
            this.$nextTick( () => {
                this.resetPromptInputHeight();
            } );
        },
        updateWorkflowGoal( user_prompt ) {
            let normalized_prompt = typeof user_prompt === 'string' ? user_prompt.trim() : '';
            if ( normalized_prompt === '' ) {
                return;
            }

            if ( this.shouldPreserveWorkflowGoalForFollowUp( normalized_prompt ) ) {
                return;
            }

            this.current_workflow_goal = normalized_prompt;
        },
        shouldPreserveWorkflowGoalForFollowUp( user_prompt ) {
            if ( typeof this.current_workflow_goal !== 'string' || this.current_workflow_goal.trim() === '' ) {
                return false;
            }

            if ( this.response_id == null || this.response_id === '' ) {
                return false;
            }

            if ( !this.wasLastAssistantTurnAskingForClarification() ) {
                return false;
            }

            return this.isShortWorkflowFollowUpPrompt( user_prompt );
        },
        wasLastAssistantTurnAskingForClarification() {
            let last_assistant_completion = this.getLastFinalizedAssistantCompletion();
            if ( !last_assistant_completion || typeof last_assistant_completion.result !== 'string' ) {
                return false;
            }

            let assistant_text = this.convertMessageHtmlToPlainText( last_assistant_completion.result ).toLowerCase();
            if ( assistant_text === '' ) {
                return false;
            }

            return assistant_text.includes( '?' )
                || assistant_text.includes( 'please reply' )
                || assistant_text.includes( 'i need one detail first' )
                || assistant_text.includes( 'what effective date' )
                || assistant_text.includes( 'what date' );
        },
        isShortWorkflowFollowUpPrompt( user_prompt ) {
            let normalized_prompt = typeof user_prompt === 'string' ? user_prompt.trim() : '';
            if ( normalized_prompt === '' ) {
                return false;
            }

            let prompt_words = normalized_prompt.split( /\s+/ ).filter( ( word ) => word !== '' );
            return normalized_prompt.length <= 40 && prompt_words.length <= 6;
        },
        getCompletion( user_prompt, user_input ) {
            user_input = this.addContext( user_prompt, user_input );
            this.assistant_request_generation += 1;
            let request_generation = this.assistant_request_generation;
            this.input_disabled = true;
            this.show_spinner = true;
            this.tt_assistant_api.getCompletion( user_input, {
                onResult: ( res ) => {
                    if ( this.isAssistantRequestCancelled( request_generation ) ) {
                        return;
                    }

                    let completion = res.getResult();

                    if ( typeof completion !== 'object' ) {
                        this.show_spinner = false;
                        this.input_disabled = false;
                        completion = {
                            role: 'assistant',
                            result: $.i18n._( 'Sorry, I encountered an error. Please try again later.' ),
                            message_id: this.tt_assistant_api.getMessageId()
                        };

                        this.tt_assistant_api.setMessageId( null ); //Reset message id, if we do not do this we can update the wrong message in UI conversation history when hitting server error.
                    }

                    if ( completion?.type === 'client_tool_request' ) {
                        this.response_id = completion.response_id ?? this.response_id;
                        this.ticket_response_id = completion.ticket_response_id ?? completion.response_id ?? this.ticket_response_id ?? null;
                        if ( this.response_id || this.ticket_response_id ) {
                            this.ensureConversationSession();
                            this.conversation_ticket_submitted = false;
                        }

                        this.handleClientToolRequest( completion, request_generation );
                        return;
                    }

                    this.show_spinner = false;
                    this.input_disabled = false;
                    completion = this.parseAnnotations( completion );
                    delete completion.reasoning_summary;
                    delete completion.reasoning_summary_preview;
                    delete completion.reasoning_summary_collapsed;
                    completion['result'] = this.renderAssistantMessage( completion['result'] );

                    this.response_id = completion.response_id ?? null;
                    this.ticket_response_id = completion.ticket_response_id ?? completion.response_id ?? this.ticket_response_id ?? null;
                    if ( this.response_id || this.ticket_response_id ) {
                        this.ensureConversationSession();
                        this.conversation_ticket_submitted = false;
                    }

                    // completion.type = this.categorizeCompletion( completion );
                    this.addCompletionToConversationHistory( completion );
                    this.processAction( completion );
                }
            } );

            ProgressBar.removeProgressBar( this.tt_assistant_api.getMessageId() );
            this.ensureInProgressMessage( this.tt_assistant_api.getMessageId() );
            this.scrollMessageToTop( this.tt_assistant_api.getMessageId() );
            setTimeout( () => {
                this.trackProgress( this.tt_assistant_api.getMessageId(), request_generation );
            }, 2000 );
        },
        async handleClientToolRequest( completion, request_generation ) {
            try {
                if ( this.isAssistantRequestCancelled( request_generation ) ) {
                    return;
                }

                this.setInProgressMessageResult( $.i18n._( 'Working on the current page...' ) );
                let client_tool_outputs = await this.executeClientToolCalls( completion?.client_tool_calls || [] );
                if ( this.isAssistantRequestCancelled( request_generation ) ) {
                    return;
                }

                if ( !Array.isArray( client_tool_outputs ) || client_tool_outputs.length === 0 ) {
                    throw new Error( 'No client tool outputs were produced.' );
                }

                Debug.Text(
                    'Submitting client tool continuation. output_count: ' + client_tool_outputs.length + ' loaded_tool_names: ' + this.formatClientToolDebugValue( Array.isArray( completion?.loaded_tool_names ) ? completion.loaded_tool_names : [] ),
                    'TTAssistant.vue',
                    'TTAssistant',
                    'handleClientToolRequest',
                    10
                );

                this.getCompletion( '', {
                    client_tool_outputs: client_tool_outputs,
                    loaded_tool_names: Array.isArray( completion?.loaded_tool_names ) ? completion.loaded_tool_names : [],
                    browser_user_goal: String( this.current_workflow_goal || this.last_user_prompt || '' ),
                } );
            } catch ( error ) {
                if ( this.isAssistantRequestCancelled( request_generation ) ) {
                    return;
                }

                Debug.Text( 'Failed to execute TT Assistant browser context tool.', 'TTAssistant.vue', 'TTAssistant', 'handleClientToolRequest', 9 );
                this.show_spinner = false;
                this.input_disabled = false;
                this.removeAllInProgressMessages();

                let error_message = {
                    role: 'system',
                    result: this.renderAssistantMessage( $.i18n._( 'Sorry, I was unable to inspect the current page. Please try again.' ) )
                };
                this.completions.push( error_message );
                this.scrollToBottom();
            }
        },
        async executeClientToolCalls( client_tool_calls ) {
            let continuation_input = [];

            for ( const client_tool_call of client_tool_calls ) {
                let tool_outputs = await this.executeClientToolCall( client_tool_call );
                if ( Array.isArray( tool_outputs ) && tool_outputs.length > 0 ) {
                    continuation_input.push( ...tool_outputs );
                }
            }

            return continuation_input;
        },
        async executeClientToolCall( client_tool_call ) {
            let tool_name = String( client_tool_call?.name ?? '' ).trim();
            let call_id = String( client_tool_call?.call_id ?? '' ).trim();
            let raw_tool_arguments = client_tool_call?.arguments;
            let tool_arguments = this.normalizeClientToolArguments( raw_tool_arguments );

            if ( tool_name === '' || call_id === '' ) {
                throw new Error( 'Client tool call is missing name or call_id.' );
            }

            Debug.Text(
                'Received client tool call. name: ' + tool_name + ' call_id: ' + call_id + ' raw_args: ' + this.formatClientToolDebugValue( raw_tool_arguments ) + ' normalized_args: ' + this.formatClientToolDebugValue( tool_arguments ),
                'TTAssistant.vue',
                'TTAssistant',
                'executeClientToolCall',
                10
            );

            if ( tool_name === 'TTAIAgent-browserGetSemanticSnapshot' ) {
                this.setInProgressMessageResult( tool_arguments?.snapshot_mode === 'navigation' ? $.i18n._( 'Inspecting navigation...' ) : $.i18n._( 'Inspecting current page...' ) );
                let active_scope_only = tool_arguments?.active_scope_only !== false;
                let snapshot_mode = tool_arguments?.snapshot_mode === 'navigation' ? 'navigation' : 'default';
                let semantic_snapshot = this.buildUserDOMSnapshot( active_scope_only, snapshot_mode );
                Debug.Text(
                    'Built semantic snapshot. actionable_elements_sent: ' + ( Array.isArray( semantic_snapshot?.actionable_elements ) ? semantic_snapshot.actionable_elements.length : 0 )
                    + ' actionable_elements_total: ' + String( semantic_snapshot?.actionable_element_count || 0 )
                    + ' actionable_elements_truncated: ' + String( !!semantic_snapshot?.actionable_elements_truncated )
                    + ' html_length: ' + String( typeof semantic_snapshot?.html === 'string' ? semantic_snapshot.html.length : 0 )
                    + ' html_truncated: ' + String( !!semantic_snapshot?.html_truncated )
                    + ' snapshot_mode: ' + String( semantic_snapshot?.snapshot_mode || '' )
                    + ' scope: ' + String( semantic_snapshot?.scope || '' )
                    + ' scope_root: ' + String( semantic_snapshot?.scope_root?.label || '' )
                    + ' url: ' + String( semantic_snapshot?.url || '' ),
                    'TTAssistant.vue',
                    'TTAssistant',
                    'executeClientToolCall',
                    10
                );
                return [
                    {
                        type: 'function_call_output',
                        call_id: call_id,
                        output: {
                            status: 'complete',
                            tool: tool_name,
                            message: 'Captured the current user page semantic snapshot.',
                            semantic_snapshot: semantic_snapshot,
                        }
                    }
                ];
            }

            if ( tool_name === 'TTAIAgent-browserGetScreenshot' ) {
                if ( this.isNavigationScreenshotRequest( tool_arguments ) ) {
                    let semantic_snapshot = this.buildUserDOMSnapshot( false, 'navigation' );
                    Debug.Text(
                        'Blocked screenshot request for navigation context. Returning navigation semantic snapshot instead. actionable_elements_sent: ' + ( Array.isArray( semantic_snapshot?.actionable_elements ) ? semantic_snapshot.actionable_elements.length : 0 ),
                        'TTAssistant.vue',
                        'TTAssistant',
                        'executeClientToolCall',
                        10
                    );
                    return [
                        {
                            type: 'function_call_output',
                            call_id: call_id,
                            output: {
                                status: 'blocked',
                                tool: tool_name,
                                message: 'Use the visible navigation semantic snapshot instead of a screenshot for left-menu navigation.',
                                semantic_snapshot: semantic_snapshot,
                            }
                        }
                    ];
                }

                this.setInProgressMessageResult( $.i18n._( 'Capturing screenshot...' ) );
                let screenshot = await this.captureUserScreenshot();
                Debug.Text(
                    'Captured user screenshot. width: ' + screenshot.width + ' height: ' + screenshot.height + ' url: ' + String( screenshot.url || '' ),
                    'TTAssistant.vue',
                    'TTAssistant',
                    'executeClientToolCall',
                    10
                );
                return [
                    {
                        type: 'function_call_output',
                        call_id: call_id,
                        output: {
                            status: 'complete',
                            tool: tool_name,
                            message: 'Captured the current user page screenshot. The screenshot image is attached in the following input item.',
                            screenshot: {
                                mime_type: 'image/png',
                                width: screenshot.width,
                                height: screenshot.height,
                                url: screenshot.url,
                                title: screenshot.title,
                            }
                        }
                    },
                    {
                        role: 'user',
                        content: [
                            {
                                type: 'input_text',
                                text: 'Current page screenshot captured for the requested browser context tool.'
                            },
                            {
                                type: 'input_image',
                                image_url: screenshot.data_url
                            }
                        ]
                    }
                ];
            }

            if ( [
                'TTAIAgent-browserClickElement',
                'TTAIAgent-browserFillField',
                'TTAIAgent-browserSendKeys',
                'TTAIAgent-browserSelectOption',
                'TTAIAgent-browserPressKey',
                'TTAIAgent-browserFocusElement',
                'TTAIAgent-browserScrollElementIntoView'
            ].includes( tool_name ) ) {
                return await this.executeConfirmedBrowserAction( tool_name, call_id, tool_arguments );
            }

            throw new Error( 'Unsupported client tool: ' + tool_name );
        },
        isNavigationScreenshotRequest( tool_arguments ) {
            let reason = this.normalizeUserDOMActionText( tool_arguments?.reason || '' );
            let main_menu_root = this.getUserDOMMainMenuRoot();
            if ( !( main_menu_root instanceof Element ) || !this.isUserDOMScopeCandidateVisible( main_menu_root ) ) {
                return false;
            }

            if ( reason === '' ) {
                return false;
            }

            return [
                'left navigation',
                'left menu',
                'menu state',
                'report section',
                'navigation position',
                'menu-navigation',
                'menu navigation'
            ].some( ( keyword ) => reason.includes( keyword ) );
        },
        normalizeClientToolArguments( tool_arguments ) {
            if ( Array.isArray( tool_arguments ) ) {
                if ( tool_arguments.length === 1 ) {
                    return this.normalizeClientToolArguments( tool_arguments[0] );
                }

                return {};
            }

            if ( typeof tool_arguments === 'string' ) {
                let trimmed_arguments = tool_arguments.trim();
                if ( trimmed_arguments === '' ) {
                    return {};
                }

                try {
                    return this.normalizeClientToolArguments( JSON.parse( trimmed_arguments ) );
                } catch ( error ) {
                    Debug.Text(
                        'Failed to parse client tool arguments JSON string. value: ' + trimmed_arguments,
                        'TTAssistant.vue',
                        'TTAssistant',
                        'normalizeClientToolArguments',
                        9
                    );
                    return {};
                }
            }

            if ( tool_arguments && typeof tool_arguments === 'object' ) {
                return tool_arguments;
            }

            return {};
        },
        async executeConfirmedBrowserAction( tool_name, call_id, tool_arguments ) {
            let requested_element = this.getUserDOMNodeById( tool_arguments?.element_id );
            let action_element = tool_name === 'TTAIAgent-browserPressKey'
                ? this.getResolvedKeyboardTarget( tool_arguments )
                : this.resolveUserDOMActionElement( requested_element, tool_name );
            let continuation_snapshot = this.getBrowserActionContinuationSnapshot( action_element || requested_element );
            let blocked_reason = this.getBlockedBrowserActionReason( tool_name, tool_arguments, action_element );
            this.setInProgressMessageResult( this.getBrowserActionStatusMessage( tool_name, tool_arguments, action_element || requested_element ) );

            Debug.Text(
                'Executing browser action. name: ' + tool_name
                + ' call_id: ' + call_id
                + ' requested_element_id: ' + String( tool_arguments?.element_id || '' )
                + ' requested_element: ' + this.describeUserDOMNode( requested_element, String( tool_arguments?.element_id || '' ) )
                + ' action_element: ' + this.describeUserDOMNode( action_element, String( tool_arguments?.element_id || '' ) ),
                'TTAssistant.vue',
                'TTAssistant',
                'executeConfirmedBrowserAction',
                10
            );

            if ( blocked_reason !== '' ) {
                Debug.Text(
                    'Browser action blocked. name: ' + tool_name + ' reason: ' + blocked_reason,
                    'TTAssistant.vue',
                    'TTAssistant',
                    'executeConfirmedBrowserAction',
                    10
                );
                return [
                    {
                        type: 'function_call_output',
                        call_id: call_id,
                        output: {
                            status: 'blocked',
                            tool: tool_name,
                            message: blocked_reason,
                            action_result: {
                                element_id: String( tool_arguments?.element_id || '' ),
                                element: this.describeUserDOMNode( action_element || requested_element, tool_arguments?.element_id )
                            },
                            semantic_snapshot: continuation_snapshot,
                        }
                    }
                ];
            }

            let was_confirmed = await this.ensureBrowserControlGranted( tool_name, tool_arguments, action_element || requested_element );
            if ( was_confirmed === false ) {
                Debug.Text(
                    'Browser control grant declined. name: ' + tool_name,
                    'TTAssistant.vue',
                    'TTAssistant',
                    'executeConfirmedBrowserAction',
                    10
                );
                return [
                    {
                        type: 'function_call_output',
                        call_id: call_id,
                        output: {
                            status: 'declined',
                            tool: tool_name,
                            message: 'The user declined browser control for this chat.',
                            semantic_snapshot: continuation_snapshot,
                        }
                    }
                ];
            }

            let execution_result = null;
            try {
                execution_result = await this.runConfirmedBrowserAction( tool_name, tool_arguments );
            } catch ( error ) {
                Debug.Text(
                    'Browser action threw error. name: ' + tool_name + ' error: ' + String( error?.message || error || '' ),
                    'TTAssistant.vue',
                    'TTAssistant',
                    'executeConfirmedBrowserAction',
                    9
                );
                execution_result = {
                    status: 'error',
                    message: error?.message || 'The requested browser action failed.',
                    action_result: {
                        element_id: String( tool_arguments?.element_id || '' )
                    }
                };
            }

            Debug.Text(
                'Browser action result. name: ' + tool_name + ' status: ' + String( execution_result?.status || '' ) + ' result: ' + this.formatClientToolDebugValue( execution_result ),
                'TTAssistant.vue',
                'TTAssistant',
                'executeConfirmedBrowserAction',
                10
            );

            return [
                {
                    type: 'function_call_output',
                    call_id: call_id,
                    output: {
                        status: execution_result.status,
                        tool: tool_name,
                        message: execution_result.message,
                        action_result: execution_result.action_result,
                        semantic_snapshot: this.getBrowserActionContinuationSnapshot( action_element || requested_element ),
                    }
                }
            ];
        },
        getBrowserActionContinuationSnapshot( element ) {
            return this.buildUserDOMSnapshot();
        },
        confirmBrowserControlGrant( message_html ) {
            return new Promise( ( resolve ) => {
                TAlertManager.showConfirmAlert(
                    message_html,
                    $.i18n._( 'Allow AI Assistant Browser Control' ),
                    ( confirmed ) => resolve( confirmed === true ),
                    $.i18n._( 'Allow' ),
                    $.i18n._( 'Deny' )
                );
            } );
        },
        async ensureBrowserControlGranted( tool_name, tool_arguments, element ) {
            if ( this.is_browser_control_granted ) {
                Debug.Text(
                    'Browser control already granted for this chat. name: ' + tool_name,
                    'TTAssistant.vue',
                    'TTAssistant',
                    'ensureBrowserControlGranted',
                    10
                );
                return true;
            }

            let confirmation_text = this.buildBrowserControlGrantMessage( tool_name, tool_arguments, element );
            let requested_action = this.buildBrowserActionConfirmationMessage( tool_name, tool_arguments, element );
            Debug.Text(
                'Prompting for browser control grant. first_tool: ' + tool_name + ' first_action: ' + requested_action,
                'TTAssistant.vue',
                'TTAssistant',
                'ensureBrowserControlGranted',
                10
            );
            let was_confirmed = await this.confirmBrowserControlGrant( confirmation_text );
            this.is_browser_control_granted = ( was_confirmed === true );

            Debug.Text(
                'Browser control grant prompt resolved. confirmed: ' + ( this.is_browser_control_granted ? 'yes' : 'no' ) + ' first_tool: ' + tool_name,
                'TTAssistant.vue',
                'TTAssistant',
                'ensureBrowserControlGranted',
                10
            );

            return this.is_browser_control_granted;
        },
        buildBrowserControlGrantMessage( tool_name, tool_arguments, element ) {
            return $.i18n._( 'Allow the AI assistant to control your browser for this conversation?' ) + '<br><br>'
                + $.i18n._( 'It will be able to navigate pages and fill normal fields, however it will not be allowed to save, submit, approve, finalize, delete, or otherwise commit changes.' );
        },
        buildBrowserActionConfirmationMessage( tool_name, tool_arguments ) {
            let requested_element = arguments.length >= 3 ? arguments[2] : this.getUserDOMNodeById( tool_arguments?.element_id );
            let element = tool_name === 'TTAIAgent-browserPressKey'
                ? ( this.getResolvedKeyboardTarget( tool_arguments ) || requested_element )
                : ( this.resolveUserDOMActionElement( requested_element, tool_name ) || requested_element );
            let element_description = this.describeUserDOMNode( element || requested_element, tool_arguments?.element_id );
            let reason = String( tool_arguments?.reason || '' ).trim();
            let action_text = '';

            switch ( tool_name ) {
                case 'TTAIAgent-browserClickElement':
                    action_text = $.i18n._( 'The AI wants to click this element:' );
                    break;
                case 'TTAIAgent-browserFillField':
                    action_text = $.i18n._( 'The AI wants to fill this field:' ) + ' <strong>' + element_description + '</strong><br><br>' +
                        $.i18n._( 'Value:' ) + ' <strong>' + this.escapeHTML( tool_arguments?.value || '' ) + '</strong>';
                    break;
                case 'TTAIAgent-browserSendKeys':
                    action_text = $.i18n._( 'The AI wants to send keyboard input' );
                    if ( element ) {
                        action_text += $.i18n._( ' to this element:' ) + ' <strong>' + element_description + '</strong>';
                    }
                    action_text += '<br><br>' + $.i18n._( 'Keys:' ) + ' <strong>' + this.escapeHTML( tool_arguments?.keys || '' ) + '</strong>';
                    break;
                case 'TTAIAgent-browserSelectOption':
                    action_text = $.i18n._( 'The AI wants to select an option in this field:' ) + ' <strong>' + element_description + '</strong><br><br>' +
                        $.i18n._( 'Option:' ) + ' <strong>' + this.escapeHTML( tool_arguments?.value || '' ) + '</strong>';
                    break;
                case 'TTAIAgent-browserFocusElement':
                    action_text = $.i18n._( 'The AI wants to focus this element:' );
                    break;
                case 'TTAIAgent-browserScrollElementIntoView':
                    action_text = $.i18n._( 'The AI wants to scroll this element into view:' );
                    break;
                case 'TTAIAgent-browserPressKey':
                    action_text = $.i18n._( 'The AI wants to press this key in this field:' ) + ' <strong>' + element_description + '</strong><br><br>' +
                        $.i18n._( 'Key:' ) + ' <strong>' + this.escapeHTML( tool_arguments?.key || '' ) + '</strong>';
                    break;
            }

            if ( tool_name === 'TTAIAgent-browserClickElement' || tool_name === 'TTAIAgent-browserFocusElement' || tool_name === 'TTAIAgent-browserScrollElementIntoView' ) {
                action_text += ' <strong>' + element_description + '</strong>';
            }

            if ( reason !== '' ) {
                action_text += '<br><br>' + $.i18n._( 'Reason:' ) + ' ' + this.escapeHTML( reason );
            }

            return action_text;
        },
        getBlockedBrowserActionReason( tool_name, tool_arguments, element ) {
            if ( tool_name === 'TTAIAgent-browserPressKey' ) {
                return this.getBlockedBrowserKeyReason( String( tool_arguments?.key || '' ), tool_arguments, element );
            }

            if ( tool_name === 'TTAIAgent-browserSendKeys' ) {
                return this.getBlockedSendUserKeysReason( tool_arguments, element );
            }

            if ( !( element instanceof Element ) ) {
                return '';
            }

            if ( tool_name !== 'TTAIAgent-browserClickElement' ) {
                return '';
            }

            let tag_name = element.tagName.toLowerCase();
            let role = String( element.getAttribute( 'role' ) || '' ).toLowerCase();
            let href = String( element.getAttribute( 'href' ) || '' ).trim();
            let input_type = tag_name === 'input' ? String( element.getAttribute( 'type' ) || '' ).toLowerCase() : '';
            let normalized_text = this.normalizeUserDOMActionText( [
                this.getUserDOMNodeLabel( element ),
                this.getUserDOMElementText( element ),
                element.getAttribute( 'aria-label' ) || '',
                element.getAttribute( 'title' ) || '',
                element.getAttribute( 'value' ) || ''
            ].join( ' ' ) );

            if ( tag_name === 'a' && href !== '' ) {
                return '';
            }

            if ( [ 'tab', 'menuitem', 'link' ].includes( role ) ) {
                return '';
            }

            if ( tag_name === 'input' && [ 'submit', 'reset' ].includes( input_type ) ) {
                return 'The page is ready for review. You must perform the final submit or save action yourself and tell the user which visible button to click.';
            }

            if ( tag_name === 'button' && ( element.getAttribute( 'type' ) || '' ).toLowerCase() === 'submit' ) {
                return 'The page is ready for review. You must perform the final submit or save action yourself and tell the user which visible button to click.';
            }

            if ( this.shouldEvaluateDangerousBrowserActionText( element ) && this.isDangerousBrowserActionText( normalized_text ) ) {
                return 'The page is ready for review. You must perform the visible save, submit, approve, delete, or other final action yourself and tell the user which button to click.';
            }

            return '';
        },
        shouldEvaluateDangerousBrowserActionText( element ) {
            if ( !( element instanceof Element ) ) {
                return false;
            }

            let tag_name = element.tagName.toLowerCase();
            let input_type = tag_name === 'input' ? String( element.getAttribute( 'type' ) || '' ).toLowerCase() : '';

            if ( [ 'button', 'summary' ].includes( tag_name ) ) {
                return true;
            }

            if ( tag_name === 'input' ) {
                return [ 'submit', 'reset', 'button', 'image' ].includes( input_type );
            }

            return false;
        },
        getBlockedBrowserKeyReason( key_name, tool_arguments, element ) {
            let normalized_key = String( key_name || '' ).trim().toLowerCase();
            if ( normalized_key === '' ) {
                return 'Keyboard control requires a valid key.';
            }

            if ( String( tool_arguments?.element_id || '' ).trim() === '' ) {
                return 'Keyboard control requires a specific target input field from the latest semantic snapshot.';
            }

            if ( !( element instanceof Element ) || !this.isAllowedBrowserKeyboardTarget( element ) ) {
                return 'Keyboard control is only allowed for a specific input field from the latest semantic snapshot.';
            }

            if ( [ 'pageup', 'pagedown', 'home', 'end' ].includes( normalized_key ) ) {
                return 'Use clicking or scroll-to-element instead of scrolling keys.';
            }

            if ( [ 'tab', 'shift+tab', 'escape', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright', 'enter', 'backspace', 'delete' ].includes( normalized_key ) ) {
                return '';
            }

            return 'Keyboard control is limited to targeted field keys. Please use clicking or field tools instead.';
        },
        getBlockedSendUserKeysReason( tool_arguments, element ) {
            let key_segments = this.parseSendUserKeysInput( tool_arguments?.keys );
            if ( key_segments.length === 0 ) {
                return 'Keyboard input requires at least one key or text value.';
            }

            if ( String( tool_arguments?.element_id || '' ).trim() === '' ) {
                return 'Keyboard input requires a specific target input field from the latest semantic snapshot.';
            }

            let target_element = element instanceof Element ? element : null;
            if ( !( target_element instanceof Element ) || !this.isAllowedBrowserKeyboardTarget( target_element ) ) {
                return 'Keyboard input is only allowed for a specific input field from the latest semantic snapshot.';
            }

            let has_enter_key = key_segments.some( ( segment ) => segment.type === 'key' && String( segment.value || '' ).trim().toLowerCase() === 'enter' );
            if ( has_enter_key && target_element instanceof Element && target_element.tagName.toLowerCase() !== 'textarea' && target_element.closest( 'form' ) ) {
                return 'The page is ready for review. You must perform the final submit or save action yourself and tell the user which visible button to click.';
            }

            let blocked_shortcut = key_segments.find( ( segment ) => {
                if ( segment.type !== 'key' ) {
                    return false;
                }

                let normalized_key = String( segment.value || '' ).trim().toLowerCase();
                return [ 'control+s', 'ctrl+s', 'meta+s', 'command+s' ].includes( normalized_key );
            } );

            if ( blocked_shortcut ) {
                return 'The page is ready for review. You must perform the final save or submit action yourself instead of using a shortcut.';
            }

            return '';
        },
        normalizeUserDOMActionText( value ) {
            return String( value || '' ).trim().toLowerCase().replace( /\s+/g, ' ' );
        },
        isDangerousBrowserActionText( normalized_text ) {
            if ( normalized_text === '' ) {
                return false;
            }

            return [
                'save',
                'submit',
                'delete',
                'remove',
                'approve',
                'finalize',
                'post',
                'commit',
                'confirm',
                'void',
                'terminate',
                'process payroll',
                'run payroll',
                'process payment',
                'submit payroll',
            ].some( ( keyword ) => normalized_text.includes( keyword ) );
        },
        async runConfirmedBrowserAction( tool_name, tool_arguments ) {
            if ( tool_name === 'TTAIAgent-browserPressKey' ) {
                let target_element = this.getResolvedKeyboardTarget( tool_arguments );
                if ( !( target_element instanceof Element ) ) {
                    throw new Error( 'Keyboard action requires a valid target field.' );
                }

                target_element.scrollIntoView( { block: 'center', inline: 'nearest' } );
                if ( typeof target_element.focus === 'function' ) {
                    target_element.focus();
                }

                this.dispatchSyntheticKeyboardEventToElement( target_element, String( tool_arguments?.key || '' ).trim() );
                await this.waitForBrowserActionFlush();
                return {
                    status: 'complete',
                    message: 'Pressed the requested keyboard key.',
                    action_result: {
                        key: String( tool_arguments?.key || '' ).trim(),
                        element_id: this.getUserDOMNodeId( target_element ) || String( tool_arguments?.element_id || '' ),
                        element: this.describeUserDOMNode( target_element, String( tool_arguments?.element_id || '' ) )
                    }
                };
            }

            if ( tool_name === 'TTAIAgent-browserSendKeys' ) {
                let target_element = this.getResolvedSendUserKeysTarget( tool_arguments );
                this.sendUserKeysToElement( target_element, tool_arguments?.keys );
                await this.waitForBrowserActionFlush();

                return {
                    status: 'complete',
                    message: 'Sent the requested keyboard input.',
                    action_result: {
                        element_id: target_element instanceof Element ? this.getUserDOMNodeId( target_element ) || String( tool_arguments?.element_id || '' ) : String( tool_arguments?.element_id || '' ),
                        element: this.describeUserDOMNode( target_element, String( tool_arguments?.element_id || '' ) ),
                        keys: String( tool_arguments?.keys || '' )
                    }
                };
            }

            let element = this.getUserDOMNodeById( tool_arguments?.element_id );
            if ( !( element instanceof Element ) ) {
                Debug.Text(
                    'Requested browser action element is missing. name: ' + tool_name + ' element_id: ' + String( tool_arguments?.element_id || '' ),
                    'TTAssistant.vue',
                    'TTAssistant',
                    'runConfirmedBrowserAction',
                    9
                );
                return {
                    status: 'error',
                    message: 'The requested page element is no longer available.',
                    action_result: {
                        element_id: String( tool_arguments?.element_id || '' )
                    }
                };
            }

            let action_element = this.resolveUserDOMActionElement( element, tool_name );
            if ( !( action_element instanceof Element ) ) {
                Debug.Text(
                    'Resolved browser action element is not actionable. name: ' + tool_name + ' requested_element: ' + this.describeUserDOMNode( element, String( tool_arguments?.element_id || '' ) ),
                    'TTAssistant.vue',
                    'TTAssistant',
                    'runConfirmedBrowserAction',
                    9
                );
                return {
                    status: 'error',
                    message: 'The requested page element is not currently actionable.',
                    action_result: {
                        element_id: String( tool_arguments?.element_id || '' ),
                        element: this.describeUserDOMNode( element, tool_arguments?.element_id )
                    }
                };
            }

            switch ( tool_name ) {
                case 'TTAIAgent-browserClickElement':
                    this.ensureAllowedUserDOMActionTarget( action_element, [ 'password', 'file' ] );
                    action_element.scrollIntoView( { block: 'center', inline: 'nearest' } );
                    if ( typeof action_element.focus === 'function' ) {
                        action_element.focus();
                    }
                    this.dispatchUserClick( action_element );
                    break;
                case 'TTAIAgent-browserFillField':
                    this.fillUserFieldElement( action_element, String( tool_arguments?.value || '' ) );
                    break;
                case 'TTAIAgent-browserSelectOption':
                    this.selectUserFieldOption( action_element, String( tool_arguments?.value || '' ) );
                    break;
                case 'TTAIAgent-browserFocusElement':
                    this.ensureAllowedUserDOMActionTarget( action_element, [] );
                    action_element.scrollIntoView( { block: 'center', inline: 'nearest' } );
                    if ( typeof action_element.focus === 'function' ) {
                        action_element.focus();
                    }
                    break;
                case 'TTAIAgent-browserScrollElementIntoView':
                    this.ensureAllowedUserDOMActionTarget( action_element, [] );
                    action_element.scrollIntoView( { block: 'center', inline: 'nearest' } );
                    break;
                default:
                    throw new Error( 'Unsupported confirmed browser action: ' + tool_name );
            }

            await this.waitForBrowserActionFlush();

            return {
                status: 'complete',
                message: 'Executed the requested browser action.',
                action_result: {
                    element_id: this.getUserDOMNodeId( action_element ) || String( tool_arguments?.element_id || '' ),
                    requested_element_id: String( tool_arguments?.element_id || '' ),
                    element: this.describeUserDOMNode( action_element, tool_arguments?.element_id )
                }
            };
        },
        formatClientToolDebugValue( value ) {
            try {
                return JSON.stringify( value );
            } catch ( error ) {
                return String( value );
            }
        },
        buildUserDOMSnapshot( active_scope_only = true, snapshot_mode = 'default' ) {
            this.user_dom_snapshot_nodes = {};
            this.user_dom_snapshot_counter = 0;
            this.user_dom_snapshot_mode = snapshot_mode === 'navigation' ? 'navigation' : 'default';
            const max_actionable_elements = this.user_dom_snapshot_mode === 'navigation' ? 120 : 60;
            let snapshot_root = this.getUserDOMSnapshotRoot( active_scope_only );
            let main_menu_root = this.shouldIncludeUserDOMMainMenu() ? this.getUserDOMMainMenuRoot() : null;

            if ( this.user_dom_snapshot_mode === 'navigation' && main_menu_root instanceof Element ) {
                snapshot_root = main_menu_root;
            }

            let html = '';
            let html_truncated = false;
            let html_parts = this.user_dom_snapshot_mode === 'navigation' ? null : [];
            /*
             * Keep a compact HTML fallback for normal page snapshots so the model can still read
             * static text that is not represented as an actionable control. This is only a fallback;
             * the semantic/actionable model remains the primary contract.
             *
             * Longer term, we should consider replacing this with an accessibility-tree export,
             * similar in spirit to Playwright's getFullAXTree/getPartialAXTree style snapshots.
             */
            this.serializeUserDOMNode( snapshot_root, html_parts );
            if ( Array.isArray( html_parts ) ) {
                html = html_parts.join( '' );
                if ( html.length > 150000 ) {
                    html = html.slice( 0, 150000 ) + '<!-- TT AI DOM snapshot truncated -->';
                    html_truncated = true;
                }
            }

            let navigation_menu = null;
            if ( this.user_dom_snapshot_mode !== 'navigation' && main_menu_root instanceof Element && main_menu_root !== snapshot_root && !main_menu_root.contains( snapshot_root ) && !snapshot_root.contains( main_menu_root ) ) {
                navigation_menu = {
                    root: this.getUserDOMSnapshotRootDescriptor( main_menu_root ),
                };
            }

            let available_tabs = this.user_dom_snapshot_mode === 'navigation' ? [] : this.getUserDOMAvailableTabs( snapshot_root );
            let contextual_actions = this.user_dom_snapshot_mode === 'navigation' ? [] : this.getUserDOMContextualActions( snapshot_root );
            let actionable_elements = this.user_dom_snapshot_mode === 'navigation'
                ? this.getUserDOMNavigationActionableElements( snapshot_root )
                : this.mergeUserDOMActionableElements( this.getUserDOMActionableElements(), available_tabs );
            if ( this.user_dom_snapshot_mode !== 'navigation' ) {
                actionable_elements = this.mergeUserDOMActionableElements( actionable_elements, contextual_actions );
            }
            let actionable_element_count = actionable_elements.length;
            let actionable_elements_truncated = false;
            if ( actionable_elements.length > max_actionable_elements ) {
                actionable_elements = actionable_elements.slice( 0, max_actionable_elements );
                actionable_elements_truncated = true;
            }

            let semantic_snapshot = this.compactSemanticSnapshotObject( {
                url: window.location.href,
                title: document.title,
                snapshot_type: 'semantic',
                snapshot_mode: this.user_dom_snapshot_mode,
                scope: active_scope_only ? 'active' : 'page',
                scope_root: this.getUserDOMSnapshotRootDescriptor( snapshot_root ),
                viewport: {
                    width: window.innerWidth,
                    height: window.innerHeight,
                    scroll_x: window.scrollX,
                    scroll_y: window.scrollY,
                },
                active_element: this.getActiveUserElementDescriptor(),
                available_tabs: available_tabs,
                actionable_elements: actionable_elements,
                actionable_element_count: actionable_element_count,
                actionable_elements_truncated: actionable_elements_truncated,
                html: html,
                html_truncated: html_truncated,
                navigation_menu: navigation_menu,
            } );

            semantic_snapshot.estimated_context_chars = this.getEstimatedSnapshotCharacterLength( semantic_snapshot );

            return semantic_snapshot;
        },
        compactSemanticSnapshotObject( value ) {
            if ( Array.isArray( value ) ) {
                return value
                    .map( ( item ) => this.compactSemanticSnapshotObject( item ) )
                    .filter( ( item ) => !this.isSemanticSnapshotOmittableValue( item ) );
            }

            if ( value && typeof value === 'object' ) {
                let compacted_object = {};
                Object.entries( value ).forEach( ( [ key, current_value ] ) => {
                    let compacted_value = this.compactSemanticSnapshotObject( current_value );
                    if ( this.isSemanticSnapshotOmittableValue( compacted_value ) ) {
                        return;
                    }

                    compacted_object[key] = compacted_value;
                } );

                return compacted_object;
            }

            return value;
        },
        isSemanticSnapshotOmittableValue( value ) {
            if ( value === null || value === undefined || value === false ) {
                return true;
            }

            if ( typeof value === 'string' ) {
                return value.trim() === '';
            }

            if ( Array.isArray( value ) ) {
                return value.length === 0;
            }

            if ( typeof value === 'object' ) {
                return Object.keys( value ).length === 0;
            }

            return false;
        },
        getEstimatedSnapshotCharacterLength( dom_snapshot ) {
            try {
                return JSON.stringify( dom_snapshot ).length;
            } catch ( error ) {
                return 0;
            }
        },
        mergeUserDOMActionableElements( actionable_elements, additional_actionable_elements ) {
            let merged_actionable_elements = Array.isArray( actionable_elements ) ? actionable_elements.slice() : [];
            let seen_element_ids = new Set( merged_actionable_elements.map( ( element ) => String( element?.element_id || '' ) ).filter( ( element_id ) => element_id !== '' ) );

            ( Array.isArray( additional_actionable_elements ) ? additional_actionable_elements : [] ).forEach( ( actionable_element ) => {
                let element_id = String( actionable_element?.element_id || '' ).trim();
                if ( element_id === '' || seen_element_ids.has( element_id ) ) {
                    return;
                }

                seen_element_ids.add( element_id );
                merged_actionable_elements.unshift( actionable_element );
            } );

            return merged_actionable_elements;
        },
        getUserDOMAvailableTabs( snapshot_root ) {
            let tab_elements = [];
            let tablist = this.getUserDOMAssociatedTabList( snapshot_root );
            if ( tablist instanceof Element ) {
                tab_elements = Array.from( tablist.querySelectorAll( '[role="tab"]' ) || [] )
                    .filter( ( element ) => this.isUserDOMScopeCandidateVisible( element ) );
            }

            return tab_elements.map( ( tab_element ) => {
                let visibility = this.getUserDOMElementVisibilityState( tab_element );
                let synthetic_id = this.getUserDOMNodeId( tab_element );
                if ( synthetic_id === '' ) {
                    synthetic_id = this.registerUserDOMNode( tab_element );
                }
                return this.compactSemanticSnapshotObject( {
                    element_id: synthetic_id,
                    action_type: 'click',
                    tag: tab_element.tagName.toLowerCase(),
                    role: tab_element.getAttribute( 'role' ) || 'tab',
                    label: this.getUserDOMNodeLabel( tab_element ),
                    text: this.getUserDOMElementText( tab_element ),
                    dom_id: tab_element.id || '',
                    selected: String( tab_element.getAttribute( 'aria-selected' ) || '' ).toLowerCase() === 'true',
                    is_visible: visibility.is_visible,
                    is_in_viewport: visibility.is_in_viewport,
                } );
            } );
        },
        getUserDOMAssociatedTabList( snapshot_root ) {
            if ( !( snapshot_root instanceof Element ) ) {
                return null;
            }

            let labelled_by = String( snapshot_root.getAttribute( 'aria-labelledby' ) || '' ).trim();
            if ( labelled_by !== '' ) {
                for ( let label_id of labelled_by.split( /\s+/ ) ) {
                    let label_element = document.getElementById( label_id );
                    let tab_element = label_element?.closest?.( '[role="tab"]' ) || null;
                    let tablist = tab_element?.closest?.( '[role="tablist"]' ) || null;
                    if ( tablist instanceof Element ) {
                        return tablist;
                    }
                }
            }

            let direct_tablist = snapshot_root.parentElement?.querySelector?.( ':scope > [role="tablist"]' ) || null;
            if ( direct_tablist instanceof Element ) {
                return direct_tablist;
            }

            return snapshot_root.closest( '.ui-tabs, .p-tabview' )?.querySelector?.( '[role="tablist"]' ) || null;
        },
        shouldIncludeUserDOMMainMenu() {
            return this.user_dom_snapshot_mode === 'navigation';
        },
        getUserDOMMainMenuRoot() {
            let menu_root = document.querySelector( '#main-menu' );
            if ( this.isUserDOMScopeCandidateVisible( menu_root ) ) {
                return menu_root;
            }

            menu_root = document.querySelector( '.layout-menu-container .layout-menu-content' );
            if ( this.isUserDOMScopeCandidateVisible( menu_root ) ) {
                return menu_root;
            }

            return null;
        },
        isUserDOMMainMenuNode( node ) {
            if ( !( node instanceof Element ) ) {
                return false;
            }

            return node.closest( '#main-menu, .layout-menu-container, .layout-menu-content, .tt-menu-search-container' ) !== null;
        },
        getUserDOMSnapshotRoot( active_scope_only = true ) {
            if ( active_scope_only !== true ) {
                return document.body;
            }

            return this.getUserDOMActiveScopeRoot() || document.body;
        },
        getUserDOMActiveScopeRoot() {
            /*
             * Prefer the user's current interaction surface over broad page containers.
             * This keeps snapshots small enough to be useful and reduces the chance that
             * the model chases unrelated controls elsewhere on the screen.
             */
            let awesomebox_candidates = Array.from( document.querySelectorAll( '.a-dropdown' ) || [] )
                .filter( ( element ) => this.isUserDOMScopeCandidateVisible( element ) );
            if ( awesomebox_candidates.length > 0 ) {
                awesomebox_candidates.sort( ( left_element, right_element ) => this.getUserDOMScopeCandidatePriority( right_element ) - this.getUserDOMScopeCandidatePriority( left_element ) );
                return awesomebox_candidates[0];
            }

            let overlay_selectors = [
                '[role="dialog"]',
                '[role="alertdialog"]',
                '.ui-dialog',
                '.p-dialog',
                '.ui-datepicker',
                '.p-datepicker',
                '.p-dropdown-panel',
                '.p-overlaypanel',
                '.ui-menu',
                '.context-menu',
                '.a-dropdown',
                '.a-dropdown-menu',
                '.a-combo-box'
            ];
            let overlay_candidates = Array.from( document.querySelectorAll( overlay_selectors.join( ', ' ) ) || [] )
                .filter( ( element ) => this.isUserDOMScopeCandidateVisible( element ) );
            if ( overlay_candidates.length > 0 ) {
                overlay_candidates.sort( ( left_element, right_element ) => this.getUserDOMScopeCandidatePriority( right_element ) - this.getUserDOMScopeCandidatePriority( left_element ) );
                return overlay_candidates[0];
            }

            let active_element = document.activeElement instanceof Element && !this.shouldExcludeUserDOMNode( document.activeElement )
                ? document.activeElement
                : null;
            if ( active_element instanceof Element ) {
                let active_tab_panel = this.getUserDOMTabPanelForTabElement( active_element );
                if ( this.isUserDOMScopeCandidateVisible( active_tab_panel ) ) {
                    return active_tab_panel;
                }

                let scoped_parent = active_element.closest( [
                    '.ui-dialog',
                    '.p-dialog',
                    '.search-panel',
                    '[role="dialog"]',
                    '[role="tabpanel"]',
                    '.ui-tabs-panel',
                    '.p-tabview-panel',
                    'form',
                    '.edit-view-tab-outside',
                    '.edit-view-tab',
                    '#contentContainer'
                ].join( ', ' ) );
                if ( this.isUserDOMScopeCandidateVisible( scoped_parent ) ) {
                    return scoped_parent;
                }
            }

            let selected_tab_candidates = Array.from( document.querySelectorAll( '[role="tab"][aria-selected="true"], .ui-tabs-active a, .ui-tabs-active[role="tab"], .p-tabview-selected a, .p-tabview-selected[role="tab"]' ) || [] );
            for ( let selected_tab of selected_tab_candidates ) {
                let selected_tab_panel = this.getUserDOMTabPanelForTabElement( selected_tab );
                if ( this.isUserDOMScopeCandidateVisible( selected_tab_panel ) ) {
                    return selected_tab_panel;
                }
            }

            let selected_panel = document.querySelector( '[role="tabpanel"][aria-hidden="false"], [role="tabpanel"]:not([aria-hidden="true"]), .ui-tabs-panel:not([aria-hidden="true"])' );
            if ( this.isUserDOMScopeCandidateVisible( selected_panel ) ) {
                return selected_panel;
            }

            let preferred_edit_view_selectors = [
                '.edit-view-tab-outside[aria-hidden="false"]',
                '.edit-view-tab-outside:not([aria-hidden="true"])',
                '.edit-view-tab-outside-sub-view[aria-hidden="false"]',
                '.edit-view-tab-outside-sub-view:not([aria-hidden="true"])',
                '.edit-view .edit-view-tab',
                '.edit-view form',
                '.edit-view',
                '.view form'
            ];
            for ( let selector of preferred_edit_view_selectors ) {
                let edit_view_root = document.querySelector( selector );
                if ( this.isUserDOMScopeCandidateVisible( edit_view_root ) ) {
                    return edit_view_root;
                }
            }

            let preferred_list_view_selectors = [
                '.view.js-generic-list-view',
                '.sub-view.js-generic-list-view',
                '.grid-div.js-generic-grid',
                '.grid-div .ui-jqgrid',
                '.grid-div'
            ];
            for ( let selector of preferred_list_view_selectors ) {
                let list_view_root = document.querySelector( selector );
                if ( this.isUserDOMScopeCandidateVisible( list_view_root ) ) {
                    return list_view_root;
                }
            }

            let content_container = document.querySelector( '#contentContainer, .layout-main, main' );
            if ( this.isUserDOMScopeCandidateVisible( content_container ) ) {
                return content_container;
            }

            return document.body;
        },
        getUserDOMTabPanelForTabElement( tab_candidate ) {
            if ( !( tab_candidate instanceof Element ) ) {
                return null;
            }

            let tab_element = tab_candidate.matches( '[role="tab"]' )
                ? tab_candidate
                : ( tab_candidate.closest?.( '[role="tab"]' ) || null );
            if ( !( tab_element instanceof Element ) ) {
                return null;
            }

            let aria_controls = String( tab_element.getAttribute( 'aria-controls' ) || '' ).trim();
            if ( aria_controls !== '' ) {
                let controlled_panel = document.getElementById( aria_controls );
                if ( controlled_panel instanceof Element ) {
                    return controlled_panel;
                }
            }

            let labelled_by = String( tab_element.id || '' ).trim();
            if ( labelled_by !== '' ) {
                let labelled_panel = document.querySelector( '[aria-labelledby="' + CSS.escape( labelled_by ) + '"]' );
                if ( labelled_panel instanceof Element ) {
                    return labelled_panel;
                }
            }

            let tab_anchor = tab_element.matches( 'a[href^="#"]' )
                ? tab_element
                : ( tab_element.querySelector?.( 'a[href^="#"]' ) || null );
            let anchor_href = String( tab_anchor?.getAttribute?.( 'href' ) || '' ).trim();
            if ( anchor_href.startsWith( '#' ) && anchor_href.length > 1 ) {
                let target_panel = document.getElementById( anchor_href.slice( 1 ) );
                if ( target_panel instanceof Element ) {
                    return target_panel;
                }
            }

            return null;
        },
        isUserDOMScopeCandidateVisible( element ) {
            if ( !( element instanceof Element ) || this.shouldExcludeUserDOMNode( element ) ) {
                return false;
            }

            if ( this.isUserDOMElementHiddenByState( element ) ) {
                return false;
            }

            let rect = element.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0;
        },
        getUserDOMScopeCandidatePriority( element ) {
            if ( !( element instanceof Element ) ) {
                return 0;
            }

            let priority = 0;
            let role = String( element.getAttribute( 'role' ) || '' ).toLowerCase();
            if ( role === 'dialog' || role === 'alertdialog' || element.classList.contains( 'ui-dialog' ) || element.classList.contains( 'p-dialog' ) ) {
                priority += 1000;
            }
            if ( element.classList.contains( 'a-dropdown' ) || element.classList.contains( 'a-dropdown-menu' ) || element.classList.contains( 'a-combo-box' ) || element.classList.contains( 'p-dropdown-panel' ) ) {
                priority += 700;
            }
            let rect = element.getBoundingClientRect();
            priority += Math.max( 0, Math.round( rect.width * rect.height / 1000 ) );
            priority += this.getUserDOMComputedZIndex( element );

            return priority;
        },
        getUserDOMComputedZIndex( element ) {
            if ( !( element instanceof Element ) ) {
                return 0;
            }

            let computed_z_index = window.getComputedStyle( element ).zIndex;
            let parsed_z_index = parseInt( computed_z_index, 10 );
            return Number.isFinite( parsed_z_index ) ? parsed_z_index : 0;
        },
        getUserDOMSnapshotRootDescriptor( element ) {
            if ( !( element instanceof Element ) ) {
                return null;
            }

            let root_label = this.getUserDOMNodeLabel( element );
            if ( this.user_dom_snapshot_mode === 'navigation' ) {
                if ( element.id === 'main-menu' ) {
                    root_label = 'Main Menu';
                } else if ( element.matches( '.layout-menu-content, .layout-menu-container' ) ) {
                    root_label = 'Navigation Menu';
                }
            }

            return {
                element_id: this.getUserDOMNodeId( element ),
                tag: element.tagName.toLowerCase(),
                label: root_label,
                dom_id: element.id || '',
                class: typeof element.className === 'string' ? element.className : '',
            };
        },
        getUserDOMActionableElements() {
            let actionable_elements = [];

            Object.entries( this.user_dom_snapshot_nodes || {} ).forEach( ( [ synthetic_id, element ] ) => {
                let actionable_descriptor = this.buildUserDOMActionableDescriptor( element, synthetic_id );
                if ( actionable_descriptor ) {
                    actionable_elements.push( actionable_descriptor );
                }
            } );

            actionable_elements.sort( ( left_item, right_item ) => {
                // Visible controls stay first for normal action selection, but hidden controls are
                // intentionally retained so the model can reason about required disclosure steps.
                if ( left_item.is_visible !== right_item.is_visible ) {
                    return left_item.is_visible ? -1 : 1;
                }

                if ( left_item.is_in_viewport !== right_item.is_in_viewport ) {
                    return left_item.is_in_viewport ? -1 : 1;
                }

                return String( left_item.label || '' ).localeCompare( String( right_item.label || '' ) );
            } );

            let deduped_actionable_elements = [];
            let seen_signatures = new Set();
            actionable_elements.forEach( ( actionable_element ) => {
                let rect = actionable_element?.rect || {};
                let signature = [
                    actionable_element.action_type || '',
                    actionable_element.label || '',
                    actionable_element.href || '',
                    actionable_element.role || '',
                    actionable_element.tag || '',
                    String( rect.top || 0 ),
                    String( rect.left || 0 ),
                    String( rect.width || 0 ),
                    String( rect.height || 0 )
                ].join( '|' );

                if ( seen_signatures.has( signature ) ) {
                    return;
                }

                seen_signatures.add( signature );
                deduped_actionable_elements.push( actionable_element );
            } );

            return deduped_actionable_elements;
        },
        getUserDOMNavigationActionableElements( menu_root ) {
            if ( !( menu_root instanceof Element ) ) {
                return [];
            }

            let navigation_actionable_elements = [];
            let seen_signatures = new Set();
            let main_menu = menu_root.matches( '#main-menu' ) ? menu_root : menu_root.querySelector( '#main-menu' );
            if ( !( main_menu instanceof Element ) ) {
                return [];
            }

            let build_navigation_descriptor = ( candidate_element ) => {
                if ( !( candidate_element instanceof Element ) ) {
                    return null;
                }

                let visibility = this.getUserDOMElementVisibilityState( candidate_element );
                if ( visibility.is_visible !== true ) {
                    return null;
                }

                let element_id = this.getUserDOMNodeId( candidate_element );
                if ( element_id === '' ) {
                    element_id = this.registerUserDOMNode( candidate_element );
                }
                if ( element_id === '' ) {
                    return null;
                }

                return this.compactSemanticSnapshotObject( {
                    element_id: element_id,
                    action_type: 'click',
                    tag: candidate_element.tagName.toLowerCase(),
                    role: candidate_element.getAttribute( 'role' ) || '',
                    label: this.getUserDOMNodeLabel( candidate_element ),
                    text: this.getUserDOMElementText( candidate_element ),
                    href: this.normalizeUserDOMHref( candidate_element.getAttribute( 'href' ) || '' ),
                    dom_id: candidate_element.id || '',
                    class: typeof candidate_element.className === 'string' ? candidate_element.className : '',
                    disabled: !!candidate_element.disabled,
                    is_visible: visibility.is_visible,
                    is_in_viewport: visibility.is_in_viewport,
                    rect: visibility.rect,
                } );
            };

            let add_navigation_candidate = ( candidate_element, menu_item_element = null, depth = 0 ) => {
                if ( !( candidate_element instanceof Element ) ) {
                    return;
                }

                if ( !this.isUserDOMScopeCandidateVisible( candidate_element ) ) {
                    return;
                }

                if ( candidate_element.matches( '.p-menu-separator, .layout-submenu-toggler' ) ) {
                    return;
                }

                let candidate_label = this.getUserDOMNodeLabel( candidate_element );
                let candidate_text = this.getUserDOMElementText( candidate_element );
                if ( candidate_label === '' && candidate_text === '' ) {
                    return;
                }

                let actionable_descriptor = build_navigation_descriptor( candidate_element );
                if ( !actionable_descriptor ) {
                    return;
                }

                if ( menu_item_element instanceof Element ) {
                    actionable_descriptor = this.compactSemanticSnapshotObject( {
                        ...actionable_descriptor,
                        menu_depth: depth,
                        expanded: menu_item_element.classList.contains( 'active-menuitem' ),
                        has_children: menu_item_element.querySelector( ':scope > ul' ) instanceof Element,
                    } );
                }

                let signature = [
                    actionable_descriptor.label || '',
                    actionable_descriptor.text || '',
                    actionable_descriptor.href || '',
                    actionable_descriptor.dom_id || ''
                ].join( '|' );
                if ( seen_signatures.has( signature ) ) {
                    return;
                }

                seen_signatures.add( signature );
                navigation_actionable_elements.push( actionable_descriptor );
            };

            let get_direct_menu_action = ( menu_item_element ) => {
                if ( !( menu_item_element instanceof Element ) || !this.isUserDOMScopeCandidateVisible( menu_item_element ) ) {
                    return null;
                }

                for ( let child_element of Array.from( menu_item_element.children || [] ) ) {
                    if ( !( child_element instanceof Element ) ) {
                        continue;
                    }

                    if ( child_element.matches( 'a, button, [role="button"], [role="menuitem"]' ) ) {
                        return child_element;
                    }
                }

                return null;
            };

            let collect_visible_navigation_branch = ( menu_list_element, depth = 0 ) => {
                if ( !( menu_list_element instanceof Element ) || !this.isUserDOMScopeCandidateVisible( menu_list_element ) ) {
                    return;
                }

                Array.from( menu_list_element.children || [] ).forEach( ( menu_item_element ) => {
                    if ( !( menu_item_element instanceof Element ) || !menu_item_element.matches( 'li' ) ) {
                        return;
                    }

                    let direct_action = get_direct_menu_action( menu_item_element );
                    add_navigation_candidate( direct_action, menu_item_element, depth );

                    let child_menu = menu_item_element.querySelector( ':scope > ul' );
                    if ( child_menu instanceof Element && menu_item_element.classList.contains( 'active-menuitem' ) && this.isUserDOMScopeCandidateVisible( child_menu ) ) {
                        collect_visible_navigation_branch( child_menu, depth + 1 );
                    }
                } );
            };

            let root_navigation_submenu = main_menu.querySelector( ':scope > li.layout-root-menuitem > ul.layout-root-submenulist' );
            let navigation_root = root_navigation_submenu instanceof Element ? root_navigation_submenu : main_menu;

            /*
             * Navigation mode mirrors the visible menu tree: top-level entries always, and deeper
             * entries only when their parent branch is expanded and actually visible.
             */
            collect_visible_navigation_branch( navigation_root, 0 );

            if ( navigation_actionable_elements.length === 0 ) {
                Array.from( navigation_root.children || [] ).forEach( ( menu_item_element ) => {
                    if ( !( menu_item_element instanceof Element ) ) {
                        return;
                    }

                    let direct_action = get_direct_menu_action( menu_item_element );
                    add_navigation_candidate( direct_action, menu_item_element, 0 );
                } );
            }

            return navigation_actionable_elements;
        },
        buildUserDOMActionableDescriptor( element, synthetic_id = '' ) {
            if ( !( element instanceof Element ) || !this.isUserDOMActionableElement( element ) ) {
                return null;
            }

            if ( this.shouldSkipUserDOMActionableElement( element ) ) {
                return null;
            }

            let visibility = this.getUserDOMElementVisibilityState( element );
            let label = this.getUserDOMNodeLabel( element );
            let text = this.getUserDOMElementText( element );
            let value = this.getUserDOMElementValue( element );
            let href = this.normalizeUserDOMHref( element.getAttribute( 'href' ) || '' );
            // Hidden controls still need descriptors so the model can see disclosure metadata.
            if ( label === '' && text === '' && value === '' && href === '' && !element.id && !element.getAttribute( 'name' ) ) {
                return null;
            }

            let element_id = String( synthetic_id || '' ).trim();
            if ( element_id === '' ) {
                element_id = this.getUserDOMNodeId( element );
            }
            if ( element_id === '' ) {
                element_id = this.registerUserDOMNode( element );
            }
            if ( element_id === '' ) {
                return null;
            }

            return this.compactSemanticSnapshotObject( {
                element_id: element_id,
                action_type: this.getUserDOMPrimaryActionType( element ),
                tag: element.tagName.toLowerCase(),
                role: element.getAttribute( 'role' ) || '',
                input_type: element.tagName.toLowerCase() === 'input' ? String( element.getAttribute( 'type' ) || '' ).toLowerCase() : '',
                label: label,
                text: text,
                value: value,
                href: href,
                name: element.getAttribute( 'name' ) || '',
                dom_id: element.id || '',
                class: typeof element.className === 'string' ? element.className : '',
                disabled: !!element.disabled,
                is_visible: visibility.is_visible,
                is_in_viewport: visibility.is_in_viewport,
                hidden_reasons: visibility.hidden_reasons,
                hidden_by_element_id: visibility.hidden_by_element_id,
                hidden_by_element_label: visibility.hidden_by_element_label,
                disclosure_element_id: visibility.disclosure_element_id,
                disclosure_element_label: visibility.disclosure_element_label,
                disclosure_chain: visibility.disclosure_chain,
                rect: visibility.rect,
            } );
        },
        getUserDOMContextualActions( snapshot_root ) {
            if ( !( snapshot_root instanceof Element ) ) {
                return [];
            }

            let contextual_containers = [
                snapshot_root.closest( '.edit-view-tab-bar' ),
                snapshot_root.closest( '.edit-view' ),
                snapshot_root.closest( '.view' ),
                snapshot_root.closest( '.sub-view' )
            ].filter( ( container, index, all_containers ) => {
                return container instanceof Element && all_containers.indexOf( container ) === index;
            } );

            let contextual_actions = [];
            let seen_action_ids = new Set();

            contextual_containers.forEach( ( container ) => {
                Array.from( container.querySelectorAll( '.context-menu-bar button, .context-menu-bar a, .context-menu-bar [role="button"], .context-menu-bar [role="menuitem"]' ) || [] ).forEach( ( action_element ) => {
                    let actionable_descriptor = this.buildUserDOMActionableDescriptor( action_element );
                    let element_id = String( actionable_descriptor?.element_id || '' ).trim();
                    if ( !actionable_descriptor || element_id === '' || seen_action_ids.has( element_id ) ) {
                        return;
                    }

                    seen_action_ids.add( element_id );
                    contextual_actions.push( actionable_descriptor );
                } );
            } );

            return contextual_actions;
        },
        shouldSkipUserDOMActionableElement( element ) {
            if ( !( element instanceof Element ) ) {
                return false;
            }

            let role = String( element.getAttribute( 'role' ) || '' ).toLowerCase();
            if ( role === 'tablist' ) {
                return true;
            }

            if ( role !== 'tab' && element.matches( '.edit-view-tab-bar, .search-panel-tab-bar, .ui-tabs, .p-tabview' ) && element.querySelector( '[role="tab"]' ) ) {
                return true;
            }

            return false;
        },
        getUserDOMElementVisibilityState( element ) {
            /*
             * Visibility here is intentionally richer than a simple boolean. The model needs both the
             * "can I act on this right now?" answer and the hidden/disclosure metadata that explains
             * how to reveal the control when it lives behind a collapsed panel or inactive tab.
             */
            let hidden_reasons = [];
            let hidden_ancestors = this.getUserDOMHiddenAncestors( element );
            let hidden_ancestor = hidden_ancestors.length > 0 ? hidden_ancestors[hidden_ancestors.length - 1] : null;
            let rect = element.getBoundingClientRect();
            let is_in_viewport = rect.width > 0
                && rect.height > 0
                && rect.bottom > 0
                && rect.right > 0
                && rect.top < window.innerHeight
                && rect.left < window.innerWidth;

            hidden_ancestors.forEach( ( current_hidden_ancestor ) => {
                hidden_reasons.push( this.getUserDOMHiddenReasonForElement( current_hidden_ancestor ) );
            } );

            if ( rect.width <= 0 || rect.height <= 0 ) {
                hidden_reasons.push( 'zero_rect' );
            }

            if ( !is_in_viewport ) {
                hidden_reasons.push( 'outside_viewport' );
            }

            hidden_reasons = Array.from( new Set( hidden_reasons.filter( ( reason ) => String( reason || '' ) !== '' ) ) );

            let disclosure_chain = this.getUserDOMDisclosureChainForElement( element, hidden_ancestors );
            let disclosure_element = disclosure_chain.length > 0 ? disclosure_chain[0] : null;

            return {
                is_visible: hidden_reasons.length === 0 || ( hidden_reasons.length === 1 && hidden_reasons[0] === 'outside_viewport' ),
                is_in_viewport: is_in_viewport,
                hidden_reasons: hidden_reasons,
                hidden_by_element_id: hidden_ancestor instanceof Element ? this.getUserDOMNodeId( hidden_ancestor ) : '',
                hidden_by_element_label: hidden_ancestor instanceof Element ? this.getUserDOMNodeLabel( hidden_ancestor ) : '',
                disclosure_element_id: disclosure_element instanceof Element ? this.getUserDOMNodeId( disclosure_element ) : '',
                disclosure_element_label: disclosure_element instanceof Element ? this.getUserDOMNodeLabel( disclosure_element ) : '',
                disclosure_chain: disclosure_chain.map( ( current_disclosure_element ) => {
                    return {
                        element_id: this.getUserDOMNodeId( current_disclosure_element ),
                        label: this.getUserDOMNodeLabel( current_disclosure_element ),
                        action_type: this.getUserDOMPrimaryActionType( current_disclosure_element ),
                    };
                } ),
                rect: {
                    top: Math.round( rect.top ),
                    left: Math.round( rect.left ),
                    width: Math.round( rect.width ),
                    height: Math.round( rect.height ),
                }
            };
        },
        getUserDOMHiddenAncestor( element ) {
            let current_element = element instanceof Element ? element : null;

            while ( current_element instanceof Element && !this.shouldExcludeUserDOMNode( current_element ) ) {
                if ( this.isUserDOMElementHiddenByState( current_element ) ) {
                    return current_element;
                }

                current_element = current_element.parentElement;
            }

            return null;
        },
        getUserDOMHiddenAncestors( element ) {
            let current_element = element instanceof Element ? element : null;
            let hidden_ancestors = [];

            while ( current_element instanceof Element && !this.shouldExcludeUserDOMNode( current_element ) ) {
                if ( this.isUserDOMElementHiddenByState( current_element ) ) {
                    hidden_ancestors.unshift( current_element );
                }

                current_element = current_element.parentElement;
            }

            return hidden_ancestors;
        },
        isUserDOMElementHiddenByState( element ) {
            if ( !( element instanceof Element ) || this.shouldExcludeUserDOMNode( element ) ) {
                return false;
            }

            if ( this.isUserDOMSearchPanelCollapsedElement( element ) ) {
                return true;
            }

            if ( element.hidden || String( element.getAttribute( 'aria-hidden' ) || '' ).toLowerCase() === 'true' ) {
                return true;
            }

            if ( element.tagName.toLowerCase() === 'details' && element.open === false ) {
                return true;
            }

            let computed_style = window.getComputedStyle( element );
            return computed_style.display === 'none'
                || computed_style.visibility === 'hidden'
                || computed_style.visibility === 'collapse'
                || computed_style.contentVisibility === 'hidden';
        },
        getUserDOMHiddenReasonForElement( element ) {
            if ( !( element instanceof Element ) ) {
                return '';
            }

            if ( this.isUserDOMSearchPanelCollapsedElement( element ) ) {
                return 'collapsed_search_panel';
            }

            if ( element.hidden ) {
                return 'hidden_attribute';
            }

            if ( String( element.getAttribute( 'aria-hidden' ) || '' ).toLowerCase() === 'true' ) {
                return 'aria_hidden';
            }

            if ( element.tagName.toLowerCase() === 'details' && element.open === false ) {
                return 'collapsed_details';
            }

            let computed_style = window.getComputedStyle( element );
            if ( computed_style.display === 'none' ) {
                return 'display_none';
            }
            if ( computed_style.visibility === 'hidden' || computed_style.visibility === 'collapse' ) {
                return 'visibility_hidden';
            }
            if ( computed_style.contentVisibility === 'hidden' ) {
                return 'content_visibility_hidden';
            }

            return 'hidden_ancestor';
        },
        getUserDOMDisclosureChainForElement( element, hidden_ancestors ) {
            let disclosure_chain = [];
            let seen_disclosure_ids = new Set();

            // Walk every hidden ancestor so the model gets the ordered reveal path, not just one guess.
            ( Array.isArray( hidden_ancestors ) ? hidden_ancestors : [] ).forEach( ( hidden_ancestor ) => {
                this.findUserDOMDisclosureControlsForContainer( hidden_ancestor, element ).forEach( ( disclosure_element ) => {
                    if ( !( disclosure_element instanceof Element ) ) {
                        return;
                    }

                    let disclosure_element_id = this.getUserDOMNodeId( disclosure_element );
                    let disclosure_key = disclosure_element_id !== '' ? disclosure_element_id : this.describeUserDOMNode( disclosure_element );
                    if ( seen_disclosure_ids.has( disclosure_key ) ) {
                        return;
                    }

                    seen_disclosure_ids.add( disclosure_key );
                    disclosure_chain.push( disclosure_element );
                } );
            } );

            return disclosure_chain;
        },
        findUserDOMDisclosureControlForContainer( container, target_element = null ) {
            let disclosure_controls = this.findUserDOMDisclosureControlsForContainer( container, target_element );
            return disclosure_controls.length > 0 ? disclosure_controls[0] : null;
        },
        findUserDOMDisclosureControlsForContainer( container, target_element = null ) {
            if ( !( container instanceof Element ) ) {
                return [];
            }

            let candidate_controls = [];
            let container_id = String( container.id || '' ).trim();
            let search_panel_controls = this.findUserDOMSearchPanelDisclosureControls( container, target_element );
            if ( search_panel_controls.length > 0 ) {
                return search_panel_controls;
            }

            if ( container_id !== '' ) {
                let escaped_id = this.escapeUserDOMSelectorIdentifier( container_id );
                candidate_controls.push( ...Array.from( document.querySelectorAll(
                    '[aria-controls="' + escaped_id + '"], [data-target="#' + escaped_id + '"], [data-bs-target="#' + escaped_id + '"], a[href="#' + escaped_id + '"]'
                ) || [] ) );
            }

            let labelled_by = String( container.getAttribute( 'aria-labelledby' ) || '' ).trim();
            if ( labelled_by !== '' ) {
                labelled_by.split( /\s+/ ).forEach( ( label_id ) => {
                    let label_element = document.getElementById( label_id );
                    if ( label_element instanceof Element ) {
                        candidate_controls.push( label_element );
                    }
                } );
            }

            if ( container.previousElementSibling instanceof Element ) {
                candidate_controls.push( container.previousElementSibling );
            }

            if ( container.parentElement?.previousElementSibling instanceof Element ) {
                candidate_controls.push( container.parentElement.previousElementSibling );
            }

            let summary_element = container.closest( 'details' )?.querySelector( ':scope > summary' );
            if ( summary_element instanceof Element ) {
                candidate_controls.push( summary_element );
            }

            let actionable_controls = [];
            for ( let candidate_control of candidate_controls ) {
                let actionable_control = this.findNearestUserDOMActionableElement( candidate_control );
                if ( actionable_control instanceof Element ) {
                    actionable_controls.push( actionable_control );
                }
            }

            return actionable_controls;
        },
        findUserDOMSearchPanelDisclosureControls( container, target_element = null ) {
            let search_panel = container.closest( '.search-panel' );
            if ( !( search_panel instanceof Element ) ) {
                return [];
            }

            let candidate_controls = [];
            let target_tab_id = this.getUserDOMSearchPanelTabIdForElement( target_element, search_panel )
                || this.getUserDOMSearchPanelTabIdForElement( container, search_panel );

            if ( target_tab_id !== '' ) {
                let escaped_target_tab_id = this.escapeUserDOMSelectorIdentifier( target_tab_id );
                let tab_anchor = search_panel.querySelector( '.search-panel-tab-bar-label a[href="#' + escaped_target_tab_id + '"]' );
                if ( tab_anchor instanceof Element ) {
                    candidate_controls.push( tab_anchor );
                }
            }

            if ( candidate_controls.length === 0 ) {
                let collapse_button = search_panel.querySelector( '#collapseBtn, .collapse-btn' );
                if ( collapse_button instanceof Element ) {
                    candidate_controls.push( collapse_button );
                }
            }

            let actionable_controls = [];
            candidate_controls.forEach( ( candidate_control ) => {
                let actionable_control = this.findNearestUserDOMActionableElement( candidate_control );
                if ( actionable_control instanceof Element ) {
                    actionable_controls.push( actionable_control );
                }
            } );

            return actionable_controls;
        },
        getUserDOMSearchPanelTabIdForElement( element, search_panel = null ) {
            if ( !( element instanceof Element ) ) {
                return '';
            }

            let target_search_panel = search_panel instanceof Element ? search_panel : element.closest( '.search-panel' );
            if ( !( target_search_panel instanceof Element ) ) {
                return '';
            }

            let current_element = element;
            while ( current_element instanceof Element && current_element !== target_search_panel && !this.shouldExcludeUserDOMNode( current_element ) ) {
                let current_id = String( current_element.id || '' ).trim();
                if ( current_id === 'basic_search' || current_id === 'adv_search' || current_id === 'saved_layout' ) {
                    return current_id;
                }
                if ( /^(basic_search|adv_search|saved_layout)_content_div$/.test( current_id ) ) {
                    return current_id.replace( /_content_div$/, '' );
                }

                current_element = current_element.parentElement;
            }

            return '';
        },
        isUserDOMSearchPanelCollapsedElement( element ) {
            if ( !( element instanceof Element ) ) {
                return false;
            }

            return element.classList.contains( 'search-panel-tab-collapse' )
                || element.classList.contains( 'search-panel-tab-outside-collapse' );
        },
        escapeUserDOMSelectorIdentifier( value ) {
            if ( window.CSS && typeof window.CSS.escape === 'function' ) {
                return window.CSS.escape( value );
            }

            return String( value || '' ).replace( /(["\\#.:[\], ])/g, '\\$1' );
        },
        serializeUserDOMNode( node, html_parts ) {
            if ( !node ) {
                return;
            }
            let should_collect_html = Array.isArray( html_parts );

            if ( node.nodeType === Node.TEXT_NODE ) {
                let text_content = typeof node.textContent === 'string' ? node.textContent : '';
                if ( should_collect_html && text_content !== '' ) {
                    html_parts.push( this.escapeHTML( text_content ) );
                }
                return;
            }

            if ( node.nodeType !== Node.ELEMENT_NODE ) {
                return;
            }

            if ( this.shouldExcludeUserDOMNode( node ) ) {
                return;
            }

            let tag_name = node.tagName.toLowerCase();
            let synthetic_id = this.registerUserDOMNode( node );
            let attributes = this.serializeUserDOMAttributes( node );
            if ( synthetic_id !== '' ) {
                attributes = ( attributes !== '' ? attributes + ' ' : '' ) + 'data-tt-ai-id="' + this.escapeHTMLAttribute( synthetic_id ) + '"';
            }
            if ( should_collect_html ) {
                // Preserve synthetic ids in the fallback HTML so it stays aligned with the actionable model.
                html_parts.push( '<' + tag_name + ( attributes !== '' ? ' ' + attributes : '' ) + '>' );
            }

            if ( this.shouldSerializeUserDOMNodeAsLeaf( node ) ) {
                if ( should_collect_html && this.isVoidElementTag( tag_name ) === false ) {
                    html_parts.push( '</' + tag_name + '>' );
                }
                return;
            }

            if ( tag_name === 'textarea' ) {
                if ( should_collect_html ) {
                    html_parts.push( this.escapeHTML( node.value || '' ) );
                }
            } else {
                Array.from( node.childNodes || [] ).forEach( ( child_node ) => {
                    this.serializeUserDOMNode( child_node, html_parts );
                } );
            }

            if ( should_collect_html && this.isVoidElementTag( tag_name ) === false ) {
                html_parts.push( '</' + tag_name + '>' );
            }
        },
        shouldSerializeUserDOMNodeAsLeaf( node ) {
            if ( !( node instanceof Element ) || this.user_dom_snapshot_mode === 'navigation' ) {
                return false;
            }

            if ( node.matches( '.save-and-continue-div' ) ) {
                return true;
            }

            if ( node.matches( '[data-tt-widget=\"awesomebox\"], .p-splitbutton' ) ) {
                return true;
            }

            return false;
        },
        serializeUserDOMAttributes( element ) {
            let attributes = [];
            let allowed_attribute_names = {
                'id': true,
                'class': true,
                'role': true,
                'type': true,
                'name': true,
                'href': true,
                'placeholder': true,
                'title': true,
                'for': true,
                'ref': true,
                'tabindex': true,
                'aria-label': true,
                'aria-labelledby': true,
                'aria-controls': true,
                'aria-expanded': true,
                'aria-hidden': true,
                'aria-selected': true,
                'aria-checked': true,
                'data-target': true,
                'data-bs-target': true,
            };

            Array.from( element.attributes || [] ).forEach( ( attribute ) => {
                let attribute_name = String( attribute?.name ?? '' );
                if ( attribute_name === '' || attribute_name.startsWith( 'on' ) ) {
                    return;
                }

                if ( allowed_attribute_names[attribute_name] !== true && !attribute_name.startsWith( 'aria-' ) ) {
                    return;
                }

                let attribute_value = String( attribute?.value ?? '' );
                if ( attribute_name === 'href' ) {
                    attribute_value = this.normalizeUserDOMHref( attribute_value );
                }
                if ( attribute_name === 'class' && attribute_value.length > 160 ) {
                    attribute_value = attribute_value.slice( 0, 160 );
                }
                if ( attribute_value.length > 500 ) {
                    attribute_value = attribute_value.slice( 0, 500 );
                }
                attributes.push( attribute_name + '="' + this.escapeHTMLAttribute( attribute_value ) + '"' );
            } );

            let tag_name = element.tagName.toLowerCase();
            if ( tag_name === 'input' ) {
                let input_type = String( element.getAttribute( 'type' ) || '' ).toLowerCase();
                if ( input_type !== 'password' && input_type !== 'file' ) {
                    attributes.push( 'value="' + this.escapeHTMLAttribute( element.value || '' ) + '"' );
                }
                if ( element.checked ) {
                    attributes.push( 'checked="checked"' );
                }
            } else if ( tag_name === 'option' ) {
                if ( element.selected ) {
                    attributes.push( 'selected="selected"' );
                }
            } else if ( tag_name === 'select' ) {
                attributes.push( 'value="' + this.escapeHTMLAttribute( element.value || '' ) + '"' );
            }

            if ( element.disabled ) {
                attributes.push( 'disabled="disabled"' );
            }

            if ( element.readOnly ) {
                attributes.push( 'readonly="readonly"' );
            }

            if ( tag_name === 'details' && element.open ) {
                attributes.push( 'open="open"' );
            }

            return Array.from( new Set( attributes ) ).join( ' ' );
        },
        shouldExcludeUserDOMNode( node ) {
            if ( !( node instanceof Element ) ) {
                return true;
            }

            let tag_name = node.tagName.toLowerCase();
            if ( [ 'script', 'style', 'noscript', 'svg', 'path', 'use', 'defs', 'symbol' ].includes( tag_name ) ) {
                return true;
            }

            if ( !this.shouldIncludeUserDOMMainMenu() && this.isUserDOMMainMenuNode( node ) ) {
                return true;
            }

            if ( node.matches( '.save-and-continue-div' ) ) {
                return true;
            }

            if ( node.matches( '.ui-tabs-panel[aria-hidden=\"true\"], .edit-view-tab-outside[aria-hidden=\"true\"], .edit-view-tab-outside-sub-view[aria-hidden=\"true\"]' ) ) {
                return true;
            }

            return node.closest( '[data-tt-assistant-root="1"], .layout-topbar, .topbar.layout-topbar' ) !== null;
        },
        registerUserDOMNode( element ) {
            if ( !( element instanceof Element ) ) {
                return '';
            }

            let existing_synthetic_id = this.getUserDOMNodeId( element );
            if ( existing_synthetic_id !== '' ) {
                return existing_synthetic_id;
            }

            let stable_synthetic_id = this.getStableUserDOMNodeId( element );
            if ( stable_synthetic_id !== '' ) {
                this.user_dom_snapshot_nodes[stable_synthetic_id] = element;
                return stable_synthetic_id;
            }

            this.user_dom_snapshot_counter += 1;
            let synthetic_id = 'tt-ai-' + this.user_dom_snapshot_counter;
            this.user_dom_snapshot_nodes[synthetic_id] = element;

            return synthetic_id;
        },
        getUserDOMNodeById( element_id ) {
            element_id = typeof element_id === 'string' ? element_id.trim() : '';
            if ( element_id === '' ) {
                return null;
            }

            return this.user_dom_snapshot_nodes[element_id] || null;
        },
        getUserDOMNodeId( target_element ) {
            if ( !( target_element instanceof Element ) ) {
                return '';
            }

            let stable_synthetic_id = this.getStableUserDOMNodeId( target_element );
            if ( stable_synthetic_id !== '' && this.user_dom_snapshot_nodes[stable_synthetic_id] === target_element ) {
                return stable_synthetic_id;
            }

            for ( let [ synthetic_id, element ] of Object.entries( this.user_dom_snapshot_nodes || {} ) ) {
                if ( element === target_element ) {
                    return synthetic_id;
                }
            }

            return '';
        },
        getStableUserDOMNodeId( element ) {
            if ( !( element instanceof Element ) ) {
                return '';
            }

            let role = String( element.getAttribute( 'role' ) || '' ).toLowerCase();
            if ( role !== 'tab' ) {
                return '';
            }

            let tablist = element.closest( '[role="tablist"]' );
            let tablist_key = this.buildStableUserDOMKey( tablist instanceof Element ? (
                tablist.id
                || tablist.getAttribute( 'aria-label' )
                || tablist.getAttribute( 'aria-labelledby' )
                || tablist.className
            ) : '' );
            let tab_key = this.buildStableUserDOMKey(
                element.getAttribute( 'aria-controls' )
                || element.id
                || element.getAttribute( 'href' )
                || this.getUserDOMNodeLabel( element )
                || this.getUserDOMElementText( element )
            );

            if ( tab_key === '' ) {
                return '';
            }

            return 'tt-ai-tab-' + ( tablist_key !== '' ? tablist_key + '-' : '' ) + tab_key;
        },
        buildStableUserDOMKey( value ) {
            return String( value || '' )
                .trim()
                .toLowerCase()
                .replace( /[^a-z0-9]+/g, '-' )
                .replace( /^-+|-+$/g, '' )
                .slice( 0, 80 );
        },
        resolveUserDOMActionElement( element, tool_name ) {
            if ( !( element instanceof Element ) ) {
                return null;
            }

            if ( tool_name === 'TTAIAgent-browserClickElement' ) {
                let tab_action_element = this.findUserDOMTabActionElement( element );
                if ( tab_action_element instanceof Element ) {
                    return tab_action_element;
                }
            }

            if ( tool_name === 'TTAIAgent-browserFillField' ) {
                return this.findUserDOMFieldElement( element );
            }

            if ( tool_name === 'TTAIAgent-browserSelectOption' ) {
                return this.findUserDOMSelectElement( element );
            }

            if ( tool_name === 'TTAIAgent-browserClickElement' && this.isUserDOMGridRowElement( element ) ) {
                return this.findUserDOMGridRowActionElement( element );
            }

            return this.findNearestUserDOMActionableElement( element );
        },
        findUserDOMTabActionElement( element ) {
            if ( !( element instanceof Element ) ) {
                return null;
            }

            let tab_element = element.matches( '[role="tab"]' )
                ? element
                : ( element.closest?.( '[role="tab"]' ) || null );
            if ( !( tab_element instanceof Element ) ) {
                return null;
            }

            let clickable_tab_child = tab_element.querySelector( 'a, button, [role="button"]' );
            if ( clickable_tab_child instanceof Element && !this.shouldExcludeUserDOMNode( clickable_tab_child ) ) {
                return clickable_tab_child;
            }

            return tab_element;
        },
        findUserDOMGridRowActionElement( row_element ) {
            if ( !( row_element instanceof Element ) || !this.isUserDOMGridRowElement( row_element ) ) {
                return null;
            }

            let primary_cell = row_element.querySelector( 'td[role="gridcell"]:not(.td_cbox), td:not(.td_cbox)' );
            if ( primary_cell instanceof Element ) {
                return primary_cell;
            }

            let checkbox = row_element.querySelector( 'input.cbox, input[type="checkbox"]' );
            if ( checkbox instanceof Element ) {
                return checkbox;
            }

            return row_element;
        },
        findNearestUserDOMActionableElement( element ) {
            let current_element = element;
            while ( current_element instanceof Element && !this.shouldExcludeUserDOMNode( current_element ) ) {
                if ( this.isUserDOMActionableElement( current_element ) ) {
                    return current_element;
                }

                current_element = current_element.parentElement;
            }

            return null;
        },
        findUserDOMFieldElement( element ) {
            if ( !( element instanceof Element ) ) {
                return null;
            }

            let current_element = element;
            while ( current_element instanceof Element && !this.shouldExcludeUserDOMNode( current_element ) ) {
                if ( this.isUserDOMTextEntryElement( current_element ) ) {
                    return current_element;
                }
                current_element = current_element.parentElement;
            }

            return element.querySelector?.( 'input:not([type="hidden"]):not([type="password"]):not([type="file"]):not([type="checkbox"]):not([type="radio"]), textarea' ) || null;
        },
        findUserDOMSelectElement( element ) {
            if ( !( element instanceof Element ) ) {
                return null;
            }

            let current_element = element;
            while ( current_element instanceof Element && !this.shouldExcludeUserDOMNode( current_element ) ) {
                if ( current_element.tagName?.toLowerCase() === 'select' ) {
                    return current_element;
                }
                current_element = current_element.parentElement;
            }

            return element.querySelector?.( 'select' ) || null;
        },
        isUserDOMActionableElement( element ) {
            if ( !( element instanceof Element ) || this.shouldExcludeUserDOMNode( element ) ) {
                return false;
            }

            let awesomebox_root = element.closest( '[data-tt-widget="awesomebox"]' );
            if ( awesomebox_root instanceof Element && awesomebox_root !== element ) {
                return false;
            }

            if ( element.matches( '.t-date-picker-div' ) ) {
                return true;
            }

            if ( this.isUserDOMGridRowElement( element ) ) {
                return true;
            }

            let tag_name = element.tagName.toLowerCase();
            let role = String( element.getAttribute( 'role' ) || '' ).toLowerCase();
            let tab_index = element.getAttribute( 'tabindex' );

            if ( tag_name === 'a' && ( element.getAttribute( 'href' ) || '' ) !== '' ) {
                return true;
            }

            if ( [ 'button', 'summary', 'select', 'textarea' ].includes( tag_name ) ) {
                return true;
            }

            if ( tag_name === 'input' ) {
                let input_type = String( element.getAttribute( 'type' ) || '' ).toLowerCase();
                return input_type !== 'hidden';
            }

            if ( [ 'button', 'link', 'tab', 'menuitem', 'option', 'checkbox', 'radio', 'switch' ].includes( role ) ) {
                return true;
            }

            if ( tab_index !== null && parseInt( tab_index, 10 ) >= 0 ) {
                return true;
            }

            return this.hasUserDOMClickHandler( element );
        },
        isUserDOMTextEntryElement( element ) {
            if ( !( element instanceof Element ) || this.shouldExcludeUserDOMNode( element ) ) {
                return false;
            }

            let tag_name = element.tagName.toLowerCase();
            if ( tag_name === 'textarea' ) {
                return true;
            }

            if ( tag_name !== 'input' ) {
                return false;
            }

            let input_type = String( element.getAttribute( 'type' ) || 'text' ).toLowerCase();
            return ![ 'hidden', 'password', 'file', 'checkbox', 'radio', 'submit', 'button', 'reset' ].includes( input_type );
        },
        isUserDOMGridRowElement( element ) {
            if ( !( element instanceof Element ) ) {
                return false;
            }

            if ( !element.matches( 'tr.jqgrow, tr.ui-widget-content' ) ) {
                return false;
            }

            return element.closest( '.ui-jqgrid-btable, .timesheet-grid, .schedule-grid-div table' ) instanceof Element;
        },
        hasUserDOMClickHandler( element ) {
            let jquery_object = window.jQuery || window.$;
            if ( !jquery_object || typeof jquery_object._data !== 'function' ) {
                return false;
            }

            let events = jquery_object._data( element, 'events' ) || null;
            if ( !events || typeof events !== 'object' ) {
                return false;
            }

            return Array.isArray( events.click ) && events.click.length > 0
                || Array.isArray( events.mousedown ) && events.mousedown.length > 0
                || Array.isArray( events.mouseup ) && events.mouseup.length > 0;
        },
        getUserDOMPrimaryActionType( element ) {
            let tag_name = element.tagName.toLowerCase();

            if ( tag_name === 'select' ) {
                return 'select';
            }

            if ( element.matches( '.t-date-picker-div' ) ) {
                return 'fill';
            }

            if ( this.isUserDOMTextEntryElement( element ) ) {
                return 'fill';
            }

            return 'click';
        },
        getUserDOMElementText( element ) {
            if ( !( element instanceof Element ) ) {
                return '';
            }

            let tag_name = element.tagName.toLowerCase();
            if ( tag_name === 'select' ) {
                let selected_option = element.options?.[element.selectedIndex] || null;
                return String( selected_option?.text || element.value || '' ).trim().replace( /\s+/g, ' ' ).slice( 0, 200 );
            }

            if ( tag_name === 'textarea' ) {
                return String( element.value || '' ).trim().replace( /\s+/g, ' ' ).slice( 0, 200 );
            }

            if ( tag_name === 'input' ) {
                let input_type = String( element.getAttribute( 'type' ) || 'text' ).toLowerCase();
                if ( [ 'checkbox', 'radio' ].includes( input_type ) ) {
                    return element.checked ? $.i18n._( 'Checked' ) : $.i18n._( 'Unchecked' );
                }

                if ( ![ 'password', 'file', 'hidden' ].includes( input_type ) ) {
                    return String( element.value || '' ).trim().replace( /\s+/g, ' ' ).slice( 0, 200 );
                }
            }

            if ( element.matches( '.t-date-picker-div' ) ) {
                let date_input = element.querySelector( 'input' );
                return String( date_input?.value || '' ).trim().replace( /\s+/g, ' ' ).slice( 0, 200 );
            }

            return String( element.innerText || element.textContent || '' ).trim().replace( /\s+/g, ' ' ).slice( 0, 200 );
        },
        getUserDOMElementValue( element ) {
            if ( !( element instanceof Element ) ) {
                return '';
            }

            let tag_name = element.tagName.toLowerCase();
            if ( tag_name === 'select' ) {
                return String( element.value || '' ).trim();
            }

            if ( tag_name === 'textarea' ) {
                return String( element.value || '' ).trim();
            }

            if ( tag_name === 'input' ) {
                let input_type = String( element.getAttribute( 'type' ) || 'text' ).toLowerCase();
                if ( [ 'checkbox', 'radio' ].includes( input_type ) ) {
                    return element.checked ? 'true' : 'false';
                }

                if ( ![ 'password', 'file', 'hidden' ].includes( input_type ) ) {
                    return String( element.value || '' ).trim();
                }
            }

            if ( element.matches( '.t-date-picker-div' ) ) {
                let date_input = element.querySelector( 'input' );
                return String( date_input?.value || '' ).trim();
            }

            return '';
        },
        getUserDOMNodeLabel( element ) {
            if ( !( element instanceof Element ) ) {
                return '';
            }

            let aria_label = String( element.getAttribute( 'aria-label' ) || '' ).trim();
            let placeholder = String( element.getAttribute( 'placeholder' ) || '' ).trim();
            let title = String( element.getAttribute( 'title' ) || '' ).trim();
            let name = String( element.getAttribute( 'name' ) || '' ).trim();
            let id = String( element.id || '' ).trim();
            let label_text = '';

            if ( id ) {
                let explicit_label = Array.from( document.getElementsByTagName( 'label' ) || [] ).find( ( label ) => {
                    return label.getAttribute( 'for' ) === id;
                } );
                if ( explicit_label ) {
                    label_text = this.getUserDOMElementText( explicit_label );
                }
            }

            if ( label_text === '' ) {
                let wrapping_label = element.closest( 'label' );
                if ( wrapping_label ) {
                    label_text = this.getUserDOMElementText( wrapping_label );
                }
            }

            if ( label_text === '' ) {
                let form_item = element.closest( '.edit-view-form-item-div' );
                let form_item_label = form_item?.querySelector?.( '.edit-view-form-item-label' ) || null;
                if ( form_item_label instanceof Element ) {
                    label_text = this.getUserDOMElementText( form_item_label );
                }
            }

            if ( label_text === '' ) {
                let form_item = element.closest( '.form-item-div' );
                let form_item_label = form_item?.querySelector?.( '.form-item-label' ) || null;
                if ( form_item_label instanceof Element ) {
                    label_text = this.getUserDOMElementText( form_item_label );
                }
            }

            let visible_text = this.getUserDOMElementText( element );
            return label_text || aria_label || placeholder || title || name || visible_text || id;
        },
        normalizeUserDOMHref( href ) {
            href = String( href || '' ).trim();
            if ( href === '' ) {
                return '';
            }

            if ( href.startsWith( '#!' ) || href.startsWith( '#' ) || href.startsWith( '/' ) ) {
                return href;
            }

            try {
                let parsed_url = new URL( href, window.location.href );
                if ( parsed_url.origin === window.location.origin ) {
                    if ( parsed_url.hash ) {
                        return parsed_url.hash;
                    }

                    return parsed_url.pathname + parsed_url.search;
                }
            } catch ( error ) {
            }

            return href;
        },
        describeUserDOMNode( element, fallback_id = '' ) {
            if ( !( element instanceof Element ) ) {
                return $.i18n._( 'Unknown element' ) + ( fallback_id ? ' (' + this.escapeHTML( fallback_id ) + ')' : '' );
            }

            let tag_name = element.tagName.toLowerCase();
            let descriptor = this.getUserDOMNodeLabel( element ) || tag_name;
            let href = this.normalizeUserDOMHref( element.getAttribute( 'href' ) || '' );
            let role = String( element.getAttribute( 'role' ) || '' ).trim();

            if ( href !== '' ) {
                descriptor += ' [' + href + ']';
            } else if ( role !== '' ) {
                descriptor += ' (' + role + ')';
            }

            return this.escapeHTML( descriptor );
        },
        ensureAllowedUserDOMActionTarget( element, blocked_input_types = [] ) {
            if ( !( element instanceof Element ) || this.shouldExcludeUserDOMNode( element ) ) {
                throw new Error( 'Requested element is not a valid user page target.' );
            }

            let tag_name = element.tagName.toLowerCase();
            if ( tag_name === 'input' ) {
                let input_type = String( element.getAttribute( 'type' ) || '' ).toLowerCase();
                if ( blocked_input_types.includes( input_type ) ) {
                    throw new Error( 'This element type is blocked from AI browser actions.' );
                }
            }
        },
        dispatchUserClick( element ) {
            [ 'mousedown', 'mouseup' ].forEach( ( event_name ) => {
                element.dispatchEvent( new MouseEvent( event_name, {
                    bubbles: true,
                    cancelable: true,
                    view: window
                } ) );
            } );

            if ( typeof element.click === 'function' ) {
                element.click();
            } else {
                element.dispatchEvent( new MouseEvent( 'click', {
                    bubbles: true,
                    cancelable: true,
                    view: window
                } ) );
            }
        },
        fillUserFieldElement( element, value ) {
            this.ensureAllowedUserDOMActionTarget( element, [ 'password', 'file', 'checkbox', 'radio' ] );

            let tag_name = element.tagName.toLowerCase();
            if ( tag_name !== 'input' && tag_name !== 'textarea' ) {
                throw new Error( 'This element does not support text entry.' );
            }

            element.scrollIntoView( { block: 'center', inline: 'nearest' } );
            if ( typeof element.focus === 'function' ) {
                element.focus();
            }

            element.value = value;
            element.dispatchEvent( new Event( 'input', { bubbles: true } ) );
            element.dispatchEvent( new Event( 'change', { bubbles: true } ) );
        },
        selectUserFieldOption( element, value ) {
            this.ensureAllowedUserDOMActionTarget( element, [] );

            if ( element.tagName.toLowerCase() !== 'select' ) {
                throw new Error( 'This element is not a select control.' );
            }

            let normalized_value = String( value ).trim().toLowerCase();
            let matched_option = Array.from( element.options || [] ).find( ( option ) => {
                return String( option.value ).trim().toLowerCase() === normalized_value
                    || String( option.text ).trim().toLowerCase() === normalized_value;
            } );

            if ( !matched_option ) {
                throw new Error( 'No matching option was found for this select control.' );
            }

            element.scrollIntoView( { block: 'center', inline: 'nearest' } );
            if ( typeof element.focus === 'function' ) {
                element.focus();
            }

            element.value = matched_option.value;
            matched_option.selected = true;
            element.dispatchEvent( new Event( 'input', { bubbles: true } ) );
            element.dispatchEvent( new Event( 'change', { bubbles: true } ) );
        },
        dispatchSyntheticKeyboardEvent( key_name ) {
            this.dispatchSyntheticKeyboardEventToElement( document.activeElement instanceof Element ? document.activeElement : document.body, key_name );
        },
        dispatchSyntheticKeyboardEventToElement( target_element, key_name ) {
            if ( key_name === '' ) {
                throw new Error( 'Keyboard action requires a key.' );
            }

            let active_element = target_element instanceof Element ? target_element : ( document.activeElement instanceof Element ? document.activeElement : document.body );
            if ( !( active_element instanceof Element ) || this.shouldExcludeUserDOMNode( active_element ) ) {
                throw new Error( 'No valid active element is available for keyboard input.' );
            }

            let keyboard_parts = key_name.split( '+' ).map( ( part ) => String( part ).trim() ).filter( ( part ) => part !== '' );
            let base_key = keyboard_parts.pop();
            let event_init = {
                key: base_key,
                bubbles: true,
                cancelable: true,
                ctrlKey: keyboard_parts.some( ( part ) => part.toLowerCase() === 'control' || part.toLowerCase() === 'ctrl' ),
                shiftKey: keyboard_parts.some( ( part ) => part.toLowerCase() === 'shift' ),
                altKey: keyboard_parts.some( ( part ) => part.toLowerCase() === 'alt' ),
                metaKey: keyboard_parts.some( ( part ) => part.toLowerCase() === 'meta' || part.toLowerCase() === 'cmd' || part.toLowerCase() === 'command' ),
            };

            active_element.dispatchEvent( new KeyboardEvent( 'keydown', event_init ) );
            active_element.dispatchEvent( new KeyboardEvent( 'keyup', event_init ) );
        },
        getResolvedSendUserKeysTarget( tool_arguments ) {
            let requested_element = this.getUserDOMNodeById( tool_arguments?.element_id );
            let action_element = this.resolveUserDOMActionElement( requested_element, 'TTAIAgent-browserFillField' );

            if ( action_element instanceof Element && this.isAllowedBrowserKeyboardTarget( action_element ) ) {
                return action_element;
            }

            if ( requested_element instanceof Element && this.isAllowedBrowserKeyboardTarget( requested_element ) ) {
                return requested_element;
            }

            throw new Error( 'No valid target element is available for keyboard input.' );
        },
        getResolvedKeyboardTarget( tool_arguments ) {
            let requested_element = this.getUserDOMNodeById( tool_arguments?.element_id );
            let action_element = this.resolveUserDOMActionElement( requested_element, 'TTAIAgent-browserFillField' );

            if ( action_element instanceof Element && this.isAllowedBrowserKeyboardTarget( action_element ) ) {
                return action_element;
            }

            if ( requested_element instanceof Element && this.isAllowedBrowserKeyboardTarget( requested_element ) ) {
                return requested_element;
            }

            return null;
        },
        isAllowedBrowserKeyboardTarget( element ) {
            if ( !( element instanceof Element ) || this.shouldExcludeUserDOMNode( element ) ) {
                return false;
            }

            if ( this.isUserDOMTextEntryElement( element ) || element.matches( '.t-date-picker-div' ) ) {
                return true;
            }

            let tag_name = element.tagName.toLowerCase();
            if ( [ 'textarea', 'select' ].includes( tag_name ) ) {
                return true;
            }

            let role = String( element.getAttribute( 'role' ) || '' ).toLowerCase();
            return role === 'combobox';
        },
        parseSendUserKeysInput( keys_input ) {
            if ( Array.isArray( keys_input ) ) {
                return keys_input.flatMap( ( key_entry ) => this.parseSendUserKeysInput( key_entry ) );
            }

            let keys_string = String( keys_input ?? '' );
            if ( keys_string.trim() === '' ) {
                return [];
            }

            let segments = [];
            let token_regex = /\{([^}]+)\}/g;
            let last_index = 0;
            let match = null;

            while ( ( match = token_regex.exec( keys_string ) ) !== null ) {
                if ( match.index > last_index ) {
                    segments.push( {
                        type: 'text',
                        value: keys_string.substring( last_index, match.index )
                    } );
                }

                segments.push( {
                    type: 'key',
                    value: String( match[1] || '' ).trim()
                } );
                last_index = token_regex.lastIndex;
            }

            if ( segments.length > 0 ) {
                if ( last_index < keys_string.length ) {
                    segments.push( {
                        type: 'text',
                        value: keys_string.substring( last_index )
                    } );
                }

                return segments.filter( ( segment ) => String( segment.value || '' ) !== '' );
            }

            if ( this.isSpecialSendUserKey( keys_string ) ) {
                return [ { type: 'key', value: keys_string.trim() } ];
            }

            return [ { type: 'text', value: keys_string } ];
        },
        isSpecialSendUserKey( key_name ) {
            let normalized_key = String( key_name || '' ).trim().toLowerCase();
            return [
                'enter', 'tab', 'shift+tab', 'escape',
                'arrowup', 'arrowdown', 'arrowleft', 'arrowright',
                'pageup', 'pagedown', 'home', 'end',
                'backspace', 'delete',
                'control+s', 'ctrl+s', 'meta+s', 'command+s'
            ].includes( normalized_key );
        },
        sendUserKeysToElement( target_element, keys_input ) {
            if ( !( target_element instanceof Element ) || this.shouldExcludeUserDOMNode( target_element ) ) {
                throw new Error( 'No valid target element is available for keyboard input.' );
            }

            target_element.scrollIntoView( { block: 'center', inline: 'nearest' } );
            if ( typeof target_element.focus === 'function' ) {
                target_element.focus();
            }

            let key_segments = this.parseSendUserKeysInput( keys_input );
            if ( key_segments.length === 0 ) {
                throw new Error( 'Keyboard input requires at least one key or text value.' );
            }

            key_segments.forEach( ( segment ) => {
                if ( segment.type === 'text' ) {
                    this.typeTextIntoUserElement( target_element, String( segment.value || '' ) );
                } else if ( segment.type === 'key' ) {
                    this.applySpecialUserKeyToElement( target_element, String( segment.value || '' ) );
                }
            } );
        },
        typeTextIntoUserElement( target_element, text_value ) {
            if ( text_value === '' ) {
                return;
            }

            this.ensureAllowedUserDOMActionTarget( target_element, [ 'password', 'file', 'checkbox', 'radio' ] );

            let tag_name = target_element.tagName.toLowerCase();
            if ( tag_name === 'input' || tag_name === 'textarea' ) {
                let current_value = String( target_element.value || '' );
                let selection_start = Number.isInteger( target_element.selectionStart ) ? target_element.selectionStart : current_value.length;
                let selection_end = Number.isInteger( target_element.selectionEnd ) ? target_element.selectionEnd : selection_start;
                let next_value = current_value.slice( 0, selection_start ) + text_value + current_value.slice( selection_end );

                for ( let character of text_value ) {
                    this.dispatchSyntheticKeyboardEventToElement( target_element, character );
                }

                target_element.value = next_value;
                if ( typeof target_element.setSelectionRange === 'function' ) {
                    let next_cursor_position = selection_start + text_value.length;
                    target_element.setSelectionRange( next_cursor_position, next_cursor_position );
                }
                target_element.dispatchEvent( new Event( 'input', { bubbles: true } ) );
                target_element.dispatchEvent( new Event( 'change', { bubbles: true } ) );
                return;
            }

            if ( target_element.isContentEditable ) {
                if ( typeof document.execCommand === 'function' ) {
                    document.execCommand( 'insertText', false, text_value );
                } else {
                    target_element.textContent = String( target_element.textContent || '' ) + text_value;
                }
                target_element.dispatchEvent( new Event( 'input', { bubbles: true } ) );
                return;
            }

            throw new Error( 'This element does not support typed keyboard input.' );
        },
        applySpecialUserKeyToElement( target_element, key_name ) {
            let normalized_key = String( key_name || '' ).trim();
            if ( normalized_key === '' ) {
                return;
            }

            let lower_key = normalized_key.toLowerCase();
            let tag_name = target_element.tagName.toLowerCase();
            if ( [ 'backspace', 'delete' ].includes( lower_key ) && ( tag_name === 'input' || tag_name === 'textarea' ) ) {
                this.ensureAllowedUserDOMActionTarget( target_element, [ 'password', 'file', 'checkbox', 'radio' ] );

                let current_value = String( target_element.value || '' );
                let selection_start = Number.isInteger( target_element.selectionStart ) ? target_element.selectionStart : current_value.length;
                let selection_end = Number.isInteger( target_element.selectionEnd ) ? target_element.selectionEnd : selection_start;
                let next_start = selection_start;
                let next_end = selection_end;

                if ( selection_start === selection_end ) {
                    if ( lower_key === 'backspace' && selection_start > 0 ) {
                        next_start = selection_start - 1;
                    } else if ( lower_key === 'delete' && selection_end < current_value.length ) {
                        next_end = selection_end + 1;
                    }
                }

                target_element.value = current_value.slice( 0, next_start ) + current_value.slice( next_end );
                if ( typeof target_element.setSelectionRange === 'function' ) {
                    target_element.setSelectionRange( next_start, next_start );
                }
                target_element.dispatchEvent( new Event( 'input', { bubbles: true } ) );
                target_element.dispatchEvent( new Event( 'change', { bubbles: true } ) );
            }

            this.dispatchSyntheticKeyboardEventToElement( target_element, normalized_key );
        },
        waitForBrowserActionFlush() {
            return new Promise( ( resolve ) => {
                window.requestAnimationFrame( () => {
                    window.setTimeout( () => resolve(), 50 );
                } );
            } );
        },
        isVoidElementTag( tag_name ) {
            return [ 'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr' ].includes( tag_name );
        },
        getActiveUserElementDescriptor() {
            let active_element = document.activeElement;
            if ( !( active_element instanceof Element ) || this.shouldExcludeUserDOMNode( active_element ) ) {
                return null;
            }

            return {
                tag: active_element.tagName.toLowerCase(),
                id: active_element.id || '',
                name: active_element.getAttribute( 'name' ) || '',
                class: active_element.className || '',
                aria_label: active_element.getAttribute( 'aria-label' ) || '',
            };
        },
        escapeHTML( value ) {
            return String( value )
                .replace( /&/g, '&amp;' )
                .replace( /</g, '&lt;' )
                .replace( />/g, '&gt;' );
        },
        escapeHTMLAttribute( value ) {
            return this.escapeHTML( value )
                .replace( /"/g, '&quot;' )
                .replace( /'/g, '&#39;' );
        },
        async captureUserScreenshot() {
            if ( typeof window.html2canvas !== 'function' ) {
                throw new Error( 'html2canvas is not available.' );
            }

            let canvas = await window.html2canvas( document.body, {
                backgroundColor: '#ffffff',
                logging: false,
                useCORS: true,
                x: window.scrollX,
                y: window.scrollY,
                width: window.innerWidth,
                height: window.innerHeight,
                ignoreElements: ( element ) => {
                    return element instanceof Element && element.closest( '[data-tt-assistant-root="1"]' ) !== null;
                }
            } );

            return {
                data_url: canvas.toDataURL( 'image/png' ),
                width: canvas.width,
                height: canvas.height,
                url: window.location.href,
                title: document.title,
            };
        },
        removeInProgressMessageByMessageId( message_id ) {
            if ( !message_id ) {
                return;
            }

            this.completions = this.completions.filter( ( completion ) => {
                return !( completion && completion.id == null && completion.message_id === message_id );
            } );
        },
        /*
         * Replace an in-progress assistant row with a final visible message so cancellation is explicit in conversation history.
         */
        replaceInProgressMessageByMessageId( message_id, replacement_completion ) {
            if ( !message_id || !replacement_completion || typeof replacement_completion !== 'object' ) {
                return;
            }

            let replacement_applied = false;
            this.completions = this.completions.map( ( completion ) => {
                if ( completion && completion.id == null && completion.message_id === message_id ) {
                    replacement_applied = true;
                    return Object.assign( {}, completion, replacement_completion, {
                        id: TTUUID.generateUUID(),
                    } );
                }

                return completion;
            } );

            if ( replacement_applied ) {
                this.scrollToBottom();
            }
        },
        removeAllInProgressMessages() {
            this.completions = this.completions.filter( ( completion ) => {
                return !( completion && completion.id == null && completion.role === 'assistant' );
            } );
        },
        parseAnnotations( completion ) {
            let result = typeof completion.result === 'string' ? completion.result : '';
            let has_generated_download_link = false;

            if ( Array.isArray( completion.annotations ) && completion.annotations.length > 0 ) {
                let annotations = completion.annotations;

                for ( let i = 0; i < annotations.length; i++ ) {
                    let annotation = annotations[i];
                    let annotation_type = String( annotation.type ?? '' ).toLowerCase();

                    if ( annotation_type === 'file_path' || annotation_type === 'container_file_citation' ) {
                        // From the OpenAI Documentation: File path annotations are created by code_interpreter and point to generated files.
                        // Render these as download links routed through APITTAssistant so auth/session handling stays server-side.
                        let annotation_text = String( annotation.text ?? '' );
                        let file_id = annotation.file_id
                            ?? annotation.file_path?.file_id
                            ?? annotation.container_file_citation?.file_id
                            ?? null;
                        let container_id = annotation.container_id
                            ?? annotation.file_path?.container_id
                            ?? annotation.container_file_citation?.container_id
                            ?? '';
                        let file_name = annotation.filename
                            ?? annotation.file_path?.filename
                            ?? annotation.container_file_citation?.filename
                            ?? annotation_text
                            ?? $.i18n._( 'generated_file' );
                        if ( file_name && file_name.includes( '/' ) ) {
                            let file_name_parts = file_name.split( '/' );
                            file_name = file_name_parts[file_name_parts.length - 1];
                        }

                        if ( file_id ) {
                            let encoded_file_id = encodeURIComponent( file_id );
                            let encoded_file_name = encodeURIComponent( file_name );
                            let encoded_container_id = encodeURIComponent( String( container_id ?? '' ) );
                            let safe_file_name = DOMPurify.sanitize( String( file_name ), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] } );
                            let download_link = `<a href="#" class="assistant-download-link" data-file-id="${encoded_file_id}" data-file-name="${encoded_file_name}" data-container-id="${encoded_container_id}"><i class="tticon tticon-file_download_black_24dp assistant-download-icon" aria-hidden="true"></i><span class="assistant-download-name">${safe_file_name}</span><span class="assistant-download-action">${$.i18n._( 'Download' )}</span></a>`;
                            let replaced_download_link = false;

                            if ( annotation_text ) {
                                //Responses may emit markdown links that point to sandbox paths; replace the full markdown link with
                                //a TimeTrex download link so the browser can fetch files via our authenticated API route.
                                let markdown_link_regex = new RegExp( `\\[[^\\]]*\\]\\(\\s*${this.escapeRegExp( annotation_text )}\\s*\\)`, 'g' );
                                let replaced_result = result.replace( markdown_link_regex, download_link );
                                if ( replaced_result !== result ) {
                                    result = replaced_result;
                                    replaced_download_link = true;
                                }
                            }

                            if ( replaced_download_link === false && annotation_text && result.includes( annotation_text ) ) {
                                result = result.replace( annotation_text, download_link );
                                replaced_download_link = true;
                            }

                            if ( replaced_download_link === false ) {
                                //Fallback appends a direct download action if no inline placeholder was found in the model text.
                                result += `\n\n${download_link}`;
                            }

                            has_generated_download_link = true;
                        } else if ( annotation_text ) {
                            //If file metadata is missing, remove dangling sandbox markers to avoid empty/broken links.
                            let markdown_link_regex = new RegExp( `\\[[^\\]]*\\]\\(\\s*${this.escapeRegExp( annotation_text )}\\s*\\)`, 'g' );
                            let replaced_result = result.replace( markdown_link_regex, '' );
                            if ( replaced_result !== result ) {
                                result = replaced_result;
                            } else {
                                result = result.replace( annotation_text, '' );
                            }
                        }
                    } else if ( annotation_type === 'file_citation' ) {
                        // From the OpenAI Documentation: File citations are created by the retrieval tool and define references to a specific quote in a specific file that was uploaded and used by the Assistant to generate the response.
                        let quote = annotation.file_citation?.quote ?? annotation.filename ?? annotation.file_id ?? null;
                        let annotation_text = annotation.text ?? null;

                        if ( !quote ) {
                            if ( annotation_text ) {
                                result = result.replace( annotation_text, '' );
                            }
                        } else {
                            let annotated_text = `<div class="annotated-text" data-anotation-id="${completion.id}"> [Source]<span class="annotation-content">${quote}</span></div>`;
                            if ( annotation_text ) {
                                result = result.replace( annotation_text, annotated_text );
                            } else {
                                result += ` ${annotated_text}`;
                            }
                        }
                    }
                }
            }

            //When we successfully created at least one download link, remove leftover sandbox markdown links
            //to prevent duplicate plain-text "Download..." lines beside our clickable link.
            if ( has_generated_download_link ) {
                result = result.replace( /\n*\[([^\]]+)\]\(\s*sandbox:[^)]+\)\n*/gi, '\n' );
                result = result.replace( /\n{3,}/g, '\n\n' );
            } else {
                //If we have no mapped file_id, downgrade sandbox links to plain text instead of empty href anchors.
                result = result.replace( /\[([^\]]+)\]\(\s*sandbox:[^)]+\)/gi, '$1' );
            }

            //AI will sometimes reference the TimeTrex admin (the docx file assistant has) guide in ways like this: "These steps will guide you to where you can find the email within the TimeTrex software【0†timetrex_admin_guide_ai.docx】."
            //This is NOT marked as an annotation by OpenAI API, but we still want to replace it with a link to the documentation.
            let guide_link = '';

            if ( PermissionManager.HelpMenuValidateAdmin() ) {
                guide_link = 'https://www.timetrex.com/h?id=admin_guide&v=' + LocalCacheData.getLoginData().application_version + '&e=' + Global.getProductEdition();
            } else if ( PermissionManager.HelpMenuValidateSupervisor() ) {
                guide_link = 'https://www.timetrex.com/h?id=supervisor_guide&v=' + LocalCacheData.getLoginData().application_version + '&e=' + Global.getProductEdition();
            } else {
                guide_link = 'https://www.timetrex.com/h?id=employee_guide&v=' + LocalCacheData.getLoginData().application_version + '&e=' + Global.getProductEdition();
            }

            result = result.replace(/【.*?\.docx】/g, ' <a href="' + guide_link + '" target="_blank">' + $.i18n._( 'View TimeTrex Documentation' ) + '</a>' );
            completion.result = result;

            return completion;
        },
        escapeRegExp( value ) {
            return String( value ).replace( /[.*+?^${}()|[\]\\]/g, '\\$&' );
        },
        onConversationClick( event ) {
            let download_link = event?.target?.closest?.( '.assistant-download-link' );
            if ( !download_link ) {
                return;
            }

            let file_id = decodeURIComponent( download_link.getAttribute( 'data-file-id' ) || '' );
            let file_name = decodeURIComponent( download_link.getAttribute( 'data-file-name' ) || '' );
            let container_id = decodeURIComponent( download_link.getAttribute( 'data-container-id' ) || '' );
            if ( file_id === '' ) {
                TAlertManager.showAlert( $.i18n._( 'Unable to download file: missing file identifier.' ) );
                return;
            }

            event.preventDefault();
            this.downloadGeneratedFile( file_id, file_name, container_id );
        },
        downloadGeneratedFile( file_id, file_name, container_id = '' ) {
            file_id = typeof file_id === 'string' ? file_id.trim() : '';
            file_name = typeof file_name === 'string' ? file_name.trim() : '';
            container_id = typeof container_id === 'string' ? container_id.trim() : '';
            if ( file_id === '' ) {
                TAlertManager.showAlert( $.i18n._( 'Unable to download file: missing file identifier.' ) );
                return;
            }

            let url = ServiceCaller.getAPIURL( 'Class=APITTAssistant&Method=downloadGeneratedFile' );
            //APIFactory v2 passes JSON payload positionally to method args (file_id, filename, container_id).
            let post_data = [ file_id, file_name, container_id ];

            //Use POST json payload so APIFactory v2 accepts the request and authentication headers are preserved.
            Global.APIFileDownload( null, null, post_data, url );
        },
        showAnnotation(event) {
            let annotated_element = event.target.closest('.annotated-text');

            if ( !annotated_element ) {
                return;
            }

            let annotation_content = annotated_element.querySelector('.annotation-content');

            if ( !annotation_content ) {
                return;
            }

            let overlay = this.$refs.annotation_overlay;
            if ( !overlay ) {
                return;
            }

            overlay.innerHTML = annotation_content.innerHTML;
            overlay.style.visibility = 'visible';
            overlay.style.opacity = '1';
            overlay.style.pointerEvents = 'auto';

            // Wait for the next DOM update cycle to ensure the overlay's dimensions are updated
            this.$nextTick(() => {
                let rect = annotated_element.getBoundingClientRect();
                let overlayWidth = overlay.offsetWidth;
                let overlayHeight = overlay.offsetHeight;

                let left_position = Math.max(rect.right - overlayWidth - 20, 0); // Ensure it doesn't go off-screen to the left
                let top_position = rect.top + window.scrollY - overlayHeight;

                overlay.style.top = `${top_position}px`;
                overlay.style.left = `${left_position}px`;
            });
        },
        hideAnnotation( event ) {
            let overlay = this.$refs.annotation_overlay;
            if ( !overlay ) {
                return;
            }

            // Determine if the mouse is moving to an element within the ai-modal
            let moving_within_ai_modal = event.relatedTarget && event.relatedTarget.closest( '.ai-modal' );

            // Only hide the overlay if we're not moving to another element within the ai-modal
            if ( !moving_within_ai_modal ) {
                overlay.style.visibility = 'hidden';
                overlay.style.opacity = '0';
                overlay.innerHTML = '';
            }
        },
        trackProgress( message_id, request_generation = null ) {
            TTAPI.APIProgressBar.getProgressBar( message_id, {
                onResult: ( result ) => {
                    if ( request_generation !== null && this.isAssistantRequestCancelled( request_generation ) ) {
                        return;
                    }

                    let res_data = result.getResult();
                    //Means error in progress bar
                    if ( res_data.hasOwnProperty( 'status_id' ) && res_data.status_id === 9999 ) {
                        return; //Progress updates have stopped
                    } else {
                        if ( res_data === true ||
                            ( Array.isArray( res_data ) && res_data.length === 0 ) || !res_data.total_iterations ||
                            typeof res_data.total_iterations !== 'number' ) {
                            return; //Progress updates have stopped
                        } else {
                            let in_progress_message = this.getCompletionByMessageId( message_id );
                            if ( in_progress_message ) {
                                let progress_message = typeof res_data.message === 'string' ? res_data.message : '';
                                let progress_message_type = this.getProgressMessageType( res_data );
                                if ( !in_progress_message.id && progress_message.trim() !== '' ) {
                                    if ( progress_message_type === 'reasoning' ) {
                                        this.setInProgressReasoningSummary( progress_message );
                                    } else {
                                        //Render streamed partial output with the same markdown pipeline as final responses
                                        //so formatting does not jump from plain text to HTML at completion.
                                        this.setInProgressMessageResult( progress_message );
                                    }
                                }
                                setTimeout( () => {
                                    if ( this.show_spinner && ( request_generation === null || !this.isAssistantRequestCancelled( request_generation ) ) ) { //Only track progress if still waiting for response
                                        this.trackProgress( message_id, request_generation );
                                    }
                                }, 2000 );
                            } else {
                                Debug.Text( 'No message found.', 'TTAssistant.vue', 'TTAssistant', 'trackProgress', 11 );
                                return; //Progress updates have stopped
                            }
                        }
                    }
                }
            } );
        },
        renderAssistantMessage( message ) {
            if ( typeof message !== 'string' || message === '' ) {
                return message;
            }

            try {
                let rendered_html = DOMPurify.sanitize( marked.parse( message ) );
                return this.enforceAssistantLinkTargets( rendered_html );
            } catch ( error ) {
                //Fallback keeps streaming robust if markdown parsing sees malformed partial tokens.
                Debug.Text( 'Failed to render assistant markdown, using sanitized fallback text.', 'TTAssistant.vue', 'TTAssistant', 'renderAssistantMessage', 11 );
                return this.enforceAssistantLinkTargets( DOMPurify.sanitize( message ) );
            }
        },
        onAssistantFeedbackClick( completion, rating ) {
            if ( !completion || completion.feedback_in_progress === true ) {
                return;
            }

            if ( rating !== 'positive' && rating !== 'negative' ) {
                return;
            }

            let chat_history = this.buildFeedbackChatHistory();
            this.sendUserFeedback( rating, chat_history, completion );
        },
        sendUserFeedback( rating, chat_history, completion = null ) {
            if ( rating !== 'positive' && rating !== 'negative' ) {
                return;
            }

            let feedback_rating = rating === 'positive' ? 1 : -1;
            let assistant_response_id = String( completion?.response_id ?? this.response_id ?? '' );

            if ( typeof chat_history !== 'string' || chat_history.trim() === '' ) {
                chat_history = $.i18n._( 'No chat history available.' );
            }

            let feedback_message = [
                'AI Assistant feedback',
                'rating: ' + rating,
                'assistant_feedback_rating: ' + feedback_rating,
                'assistant_response_id: ' + assistant_response_id,
                'assistant_local_conversation_id: ' + String( completion?.id ?? '' ),
                'assistant_message_id: ' + String( completion?.message_id ?? '' ),
                '',
                'chat_history:',
                chat_history
            ].join( '\n' );

            // Keep payload size bounded so long chats still submit reliably in production.
            if ( feedback_message.length > 12000 ) {
                feedback_message = feedback_message.slice( feedback_message.length - 12000 );
            }

            if ( completion ) {
                completion.feedback_in_progress = true;
            }
            Debug.Text( 'Submitting assistant feedback. rating: ' + rating + ' assistant_response_id: ' + assistant_response_id, 'TTAssistant.vue', 'TTAssistant', 'sendUserFeedback', 10 );

            TTAPI.APIUser.setUserFeedbackRating( feedback_rating, feedback_message, {
                onResult: ( res ) => {
                    if ( completion ) {
                        completion.feedback_in_progress = false;
                    }

                    if ( res && res.isValid && res.isValid() ) {
                        if ( completion ) {
                            completion.user_feedback_rating = rating;
                        }
                    } else {
                        Debug.Text( 'Failed to submit assistant feedback.', 'TTAssistant.vue', 'TTAssistant', 'sendUserFeedback', 9 );
                        TAlertManager.showAlert( this.labels.feedback_submit_error );
                    }
                }
            } );
        },
        ensureConversationSession() {
            if ( this.conversation_session_id ) {
                return;
            }

            this.conversation_session_id = TTUUID.generateUUID();
            this.conversation_started_epoch = Math.floor( Date.now() / 1000 );
            this.conversation_ticket_submitted = false;
        },
        isAssistantRequestCancelled( request_generation ) {
            if ( request_generation == null ) {
                return false;
            }

            return request_generation <= this.cancelled_request_generation;
        },
        resetConversationTracking() {
            this.clearHiddenConversationFinalizeTimer();
            this.response_id = null;
            this.ticket_response_id = null;
            this.conversation_session_id = null;
            this.conversation_started_epoch = null;
            this.conversation_ticket_submitted = false;
            // Reset the close-trigger guard with the rest of the conversation tracking.
            // A new conversation gets a new session id and should be eligible independently.
            this.feedback_prompt_conversation_session_id = null;
            this.is_browser_control_granted = false;
            this.last_user_prompt = '';
            this.current_workflow_goal = '';
        },
        /**
         * Count only user-authored prompts for the feedback threshold.
         *
         * this.completions contains more than just user turns: starter prompt choices,
         * assistant replies, in-progress messages, and error messages can all appear in
         * the same array. The close-trigger should represent a meaningful conversation,
         * so the threshold is based on actual prompts submitted by the user.
         */
        getAssistantUserTurnCount() {
            return this.completions.filter( ( completion ) => {
                return completion && completion.role === 'user';
            } ).length;
        },
        /**
         * Decide if closing the assistant should route through the feedback popup.
         *
         * This helper only decides whether this close event is eligible to ask for
         * feedback. It does not bypass the server-side `prompt_for_feedback` flag;
         * triggerFeedbackOnAssistantClose() still calls the normal TFeedback entry point,
         * and TFeedback suppresses the popup unless that flag is currently true.
         */
        shouldTriggerFeedbackOnAssistantClose( was_request_in_flight = false ) {
            // hideModal() cancels active requests before it hides the panel. The
            // `was_request_in_flight` snapshot preserves the pre-cancel state so a close
            // that interrupted the assistant does not look like a completed conversation.
            if ( was_request_in_flight === true || this.is_request_in_flight === true ) {
                return false;
            }

            // Three user turns means the person had a real back-and-forth with the
            // assistant. Opening the panel, clicking a starter prompt, or asking one
            // quick question should not be enough to surface the review prompt.
            if ( this.getAssistantUserTurnCount() < 3 ) {
                return false;
            }

            // Without a tracked session id we cannot reliably apply the once-per-
            // conversation guard, so avoid triggering from ambiguous state.
            if ( this.conversation_session_id == null ) {
                return false;
            }

            // Reopening and closing the same conversation should not keep invoking the
            // feedback flow. A new chat resets this value with the rest of the
            // conversation tracking and can become eligible on its own.
            return this.feedback_prompt_conversation_session_id !== this.conversation_session_id;
        },
        /**
         * Route an eligible assistant close through the existing feedback popup gate.
         *
         * This intentionally does not pass `prompt_for_feedback: true`. The feedback
         * widget reads LocalCacheData.getLoginUser().prompt_for_feedback and only shows
         * when the backend has made the current user eligible. The assistant close event
         * is only a new client-side opportunity to ask, not a force-show override.
         */
        triggerFeedbackOnAssistantClose( was_request_in_flight = false ) {
            if ( this.shouldTriggerFeedbackOnAssistantClose( was_request_in_flight ) !== true ) {
                return;
            }

            // Mark this conversation before calling TFeedback so even if the user
            // dismisses the popup, another close of the same conversation will not make
            // a second attempt.
            this.feedback_prompt_conversation_session_id = this.conversation_session_id;
            $().TFeedback( {
                // Keep the analytics/API source stable instead of prefixing it with the
                // currently open TimeTrex view id.
                source: 'AIAssistant@Close',
                force_source: true
            } );
        },
        getLastFinalizedAssistantCompletion() {
            let assistant_completions = this.completions.filter( ( completion ) => {
                return completion && completion.role === 'assistant' && completion.id;
            } );

            if ( assistant_completions.length === 0 ) {
                return null;
            }

            return assistant_completions[assistant_completions.length - 1];
        },
        getConversationTokenCount() {
            return this.completions.reduce( ( total, completion ) => {
                let tokens_used = parseInt( completion?.tokens_used, 10 );
                if ( !Number.isFinite( tokens_used ) || tokens_used <= 0 ) {
                    return total;
                }

                return total + tokens_used;
            }, 0 );
        },
        getConversationTicketResponseId() {
            let response_id = typeof this.response_id === 'string' ? this.response_id.trim() : '';
            if ( response_id !== '' ) {
                return response_id;
            }

            let ticket_response_id = typeof this.ticket_response_id === 'string' ? this.ticket_response_id.trim() : '';
            if ( ticket_response_id !== '' ) {
                return ticket_response_id;
            }

            return '';
        },
        finalizeConversation( end_reason, async = true, use_page_unload_transport = null ) {
            if ( this.conversation_ticket_submitted === true ) {
                return;
            }

            let response_id = this.getConversationTicketResponseId();
            if ( response_id === '' ) {
                return;
            }

            let last_assistant_completion = this.getLastFinalizedAssistantCompletion();
            let conversation_payload = {
                response_id: response_id,
                local_conversation_id: this.conversation_session_id || TTUUID.generateUUID(),
                end_reason: String( end_reason || 'unknown' ),
                started_epoch: this.conversation_started_epoch || Math.floor( Date.now() / 1000 ),
                ended_epoch: Math.floor( Date.now() / 1000 ),
                assistant_message_id: String( last_assistant_completion?.message_id ?? '' ),
                assistant_local_completion_id: String( last_assistant_completion?.id ?? '' ),
                conversation_history: this.buildConversationHistoryForTicket(),
                ticket_response_id: typeof this.ticket_response_id === 'string' ? this.ticket_response_id.trim() : '',
                conversation_tokens_used: this.getConversationTokenCount(),
                error_summary: this.buildConversationErrorSummary(),
                internal_error_summary: this.buildConversationInternalErrorSummary(),
            };

            this.conversation_ticket_submitted = true;
            Debug.Text( 'Finalizing assistant conversation. response_id: ' + conversation_payload.response_id + ' end_reason: ' + conversation_payload.end_reason + ' conversation_tokens_used: ' + conversation_payload.conversation_tokens_used, 'TTAssistant.vue', 'TTAssistant', 'finalizeConversation', 10 );
            if ( use_page_unload_transport === null ) {
                use_page_unload_transport = ( async === false );
            }

            if ( use_page_unload_transport === true ) {
                // Browsers block sync XHR during page dismissal, so use beacon/keepalive for unload finalization.
                ServiceCaller.callDuringPageUnload( 'APITTAssistant', 'setTTAssistantConversation', conversation_payload );
                return;
            }

            TTAPI.APITTAssistant.setTTAssistantConversation( conversation_payload, {
                async: async,
                onResult: ( res ) => {
                    if ( !res || !res.isValid || !res.isValid() ) {
                        this.conversation_ticket_submitted = false;
                    }
                },
                onError: () => {
                    this.conversation_ticket_submitted = false;
                }
            } );
        },
        /*
         * Cancel the delayed hidden-panel finalization timer when the panel is reopened or the conversation resets.
         */
        clearHiddenConversationFinalizeTimer() {
            if ( this.hidden_conversation_finalize_timeout_id != null ) {
                clearTimeout( this.hidden_conversation_finalize_timeout_id );
                this.hidden_conversation_finalize_timeout_id = null;
            }
        },
        /*
         * If the assistant pane stays hidden, submit the conversation after a grace period without clearing in-session history.
         */
        scheduleHiddenConversationFinalize() {
            this.clearHiddenConversationFinalizeTimer();

            if ( this.is_visible !== false ) {
                return;
            }

            this.hidden_conversation_finalize_timeout_id = setTimeout( () => {
                this.hidden_conversation_finalize_timeout_id = null;
                if ( this.is_visible === false ) {
                    this.finalizeConversation( 'assistant_hidden_timeout' );
                }
            }, this.hidden_conversation_finalize_delay_ms );
        },
        onWindowPageHide() {
            this.finalizeConversation( 'page_unload', false );
        },
        onWindowBeforeUnload() {
            this.finalizeConversation( 'page_unload', false );
        },
        buildConversationHistoryForTicket() {
            let login_user = LocalCacheData.getLoginUser() || {};
            let user_first_name = String( login_user.first_name ?? '' ).trim();
            if ( user_first_name === '' ) {
                user_first_name = 'User';
            }

            let history = this.completions
                .filter( ( completion ) => {
                    if ( !completion || completion.type === 'choice' ) {
                        return false;
                    }

                    if ( completion.role === 'assistant' && !completion.id ) {
                        return false;
                    }

                    return completion.role === 'assistant' || completion.role === 'user' || completion.role === 'system';
                } )
                .map( ( completion ) => {
                    let role_label = user_first_name;
                    if ( completion.role === 'assistant' ) {
                        role_label = 'Assistant';
                    } else if ( completion.role === 'system' ) {
                        role_label = 'System';
                    }
                    let message_html = typeof completion.result === 'string' ? completion.result.trim() : '';

                    if ( message_html === '' ) {
                        return '';
                    }

                    return '<div class="ticket-conversation-turn"><p><strong>' + role_label + ':</strong></p><div>' + message_html + '</div></div>';
                } )
                .filter( ( turn_html ) => turn_html !== '' )
                .join( '<hr>' );

            return history;
        },
        buildConversationErrorSummary() {
            let error_messages = this.completions
                .map( ( completion ) => {
                    if ( !completion || typeof completion !== 'object' ) {
                        return '';
                    }

                    if ( typeof completion.error_message === 'string' && completion.error_message.trim() !== '' ) {
                        return completion.error_message.trim();
                    }

                    if ( completion.role === 'system' ) {
                        return this.convertMessageHtmlToPlainText( completion.result );
                    }

                    return '';
                } )
                .filter( ( message ) => typeof message === 'string' && message.trim() !== '' );

            return error_messages.join( '\n\n' );
        },
        buildConversationInternalErrorSummary() {
            let error_messages = this.completions
                .map( ( completion ) => {
                    if ( !completion || typeof completion !== 'object' ) {
                        return '';
                    }

                    if ( typeof completion.internal_error_message === 'string' ) {
                        return completion.internal_error_message.trim();
                    }

                    return '';
                } )
                .filter( ( message ) => typeof message === 'string' && message !== '' );

            return error_messages.join( '\n\n' );
        },
        buildFeedbackChatHistory() {
            let history = this.completions
                .filter( ( completion ) => {
                    if ( !completion || completion.type === 'choice' ) {
                        return false;
                    }

                    if ( completion.role === 'assistant' && !completion.id ) {
                        return false; //Skip temporary "Thinking..." rows so feedback includes only finalized responses.
                    }

                    return completion.role === 'assistant' || completion.role === 'user' || completion.role === 'system';
                } )
                .map( ( completion ) => {
                    let role = String( completion.role ?? 'system' ).toUpperCase();
                    let text = this.convertMessageHtmlToPlainText( completion.result );
                    return role + ': ' + text;
                } )
                .join( '\n\n' );

            // Retain the tail of chat history because the most recent turns are typically what users are rating.
            if ( history.length > 10000 ) {
                history = '[history_truncated]\n' + history.slice( history.length - 10000 );
            }

            return history;
        },
        convertMessageHtmlToPlainText( message_html ) {
            if ( typeof message_html !== 'string' || message_html === '' ) {
                return '';
            }

            let container = document.createElement( 'div' );
            container.innerHTML = message_html.replace( /<br\s*\/?>/gi, '\n' );
            return String( container.textContent || container.innerText || '' ).replace( /\u00A0/g, ' ' ).replace( /\n{3,}/g, '\n\n' ).trim();
        },
        enforceAssistantLinkTargets( html ) {
            if ( typeof html !== 'string' || html.includes( '<a' ) === false ) {
                return html;
            }

            let container = document.createElement( 'div' );
            container.innerHTML = html;

            container.querySelectorAll( 'a' ).forEach( ( anchor ) => {
                // Keep assistant-generated file downloads handled by onConversationClick in the same context.
                if ( anchor.classList.contains( 'assistant-download-link' ) ) {
                    return;
                }

                anchor.setAttribute( 'target', '_blank' );
                anchor.setAttribute( 'rel', 'noopener noreferrer' );
            } );

            return container.innerHTML;
        },
        addInProgressMessageToConversationHistory( message_id ) {
            let in_progress_message = {
                role: 'assistant',
                // Keep the same rendered HTML shape used by streaming updates to avoid subtle border/layout jitter.
                result: this.renderAssistantMessage( $.i18n._( 'Thinking...' ) ),
                id: null,
                message_id: message_id
            };

            this.completions.push( in_progress_message );
        },
        getInProgressCompletion() {
            return this.completions.find( ( completion ) => {
                return completion && completion.role === 'assistant' && completion.id == null;
            } ) || null;
        },
        ensureInProgressMessage( message_id ) {
            let in_progress_message = this.getInProgressCompletion();
            if ( in_progress_message ) {
                in_progress_message.message_id = message_id;
                return in_progress_message;
            }

            this.addInProgressMessageToConversationHistory( message_id );
            return this.getInProgressCompletion();
        },
        setInProgressMessageResult( message ) {
            if ( typeof message !== 'string' || message.trim() === '' ) {
                return;
            }

            let in_progress_message = this.getInProgressCompletion();
            if ( !in_progress_message ) {
                return;
            }

            let rendered_message = this.renderAssistantMessage( message );
            if ( in_progress_message.result !== rendered_message ) {
                in_progress_message.result = rendered_message;
                this.scrollMessageToTop( in_progress_message.message_id || '' );
            }
        },
        getProgressMessageType( progress_data ) {
            let private_data = progress_data?.private_data ?? null;
            if ( typeof private_data === 'string' && private_data.trim() !== '' ) {
                try {
                    private_data = JSON.parse( private_data );
                } catch ( error ) {
                    private_data = null;
                }
            }

            let message_type = typeof private_data?.message_type === 'string' ? private_data.message_type.trim().toLowerCase() : '';
            if ( message_type === 'reasoning' || message_type === 'output' || message_type === 'status' ) {
                return message_type;
            }

            return 'status';
        },
        setInProgressReasoningSummary( message ) {
            if ( typeof message !== 'string' || message.trim() === '' ) {
                return;
            }

            let in_progress_message = this.getInProgressCompletion();
            if ( !in_progress_message ) {
                return;
            }

            let rendered_message = this.renderAssistantMessage( message );
            this.setInProgressMessageResult( this.buildReasoningSummaryPreview( rendered_message ) );
        },
        /*
         * Build a compact one-line reasoning preview so the assistant can show a short status summary without exposing full reasoning details.
         */
        buildReasoningSummaryPreview( reasoning_summary_html ) {
            let reasoning_text = this.convertMessageHtmlToPlainText( reasoning_summary_html );
            if ( reasoning_text === '' ) {
                return this.labels.reasoning_summary;
            }

            let first_sentence_match = reasoning_text.match( /^(.+?[.!?])(?:\s|$)/ );
            let preview = first_sentence_match ? first_sentence_match[1] : reasoning_text.split( '\n' )[0];
            preview = preview.trim();
            if ( preview.length > 120 ) {
                preview = preview.slice( 0, 117 ).trimEnd() + '...';
            }

            if ( preview !== '' && !preview.endsWith( '...' ) ) {
                preview = preview.replace( /[.!?]+$/, '' ).trimEnd();
                preview += '...';
            }

            return preview !== '' ? preview : this.labels.reasoning_summary;
        },
        getBrowserActionStatusMessage( tool_name, tool_arguments, element ) {
            let element_label = this.getUserDOMNodeLabel( element ) || this.getUserDOMElementText( element ) || '';
            let escaped_element_label = this.escapeHTML( String( element_label || '' ).trim() );
            let with_element = ( message ) => {
                if ( escaped_element_label === '' ) {
                    return message;
                }

                return message + ' <strong>' + escaped_element_label + '</strong>';
            };

            switch ( tool_name ) {
                case 'TTAIAgent-browserClickElement':
                    return with_element( $.i18n._( 'Clicking' ) );
                case 'TTAIAgent-browserFillField':
                    return with_element( $.i18n._( 'Filling' ) );
                case 'TTAIAgent-browserSendKeys':
                    return with_element( $.i18n._( 'Typing into' ) );
                case 'TTAIAgent-browserSelectOption':
                    return with_element( $.i18n._( 'Selecting an option in' ) );
                case 'TTAIAgent-browserPressKey':
                    return $.i18n._( 'Pressing a key...' );
                case 'TTAIAgent-browserFocusElement':
                    return with_element( $.i18n._( 'Focusing' ) );
                case 'TTAIAgent-browserScrollElementIntoView':
                    return with_element( $.i18n._( 'Scrolling to' ) );
                default:
                    return $.i18n._( 'Working on the current page...' );
            }
        },
        addCompletionToConversationHistory( completion ) {
            let in_progress_message = this.getCompletionByMessageId( completion.message_id );
            if ( in_progress_message ) {
                in_progress_message = Object.assign( in_progress_message, completion );
            }

            //If there is no in_progress_message then the user cleared the conversation history but this message came in right after.
            //Just ignore it, as user no longer wants to see it if they cleared the history. Otherwise, it can be odd to see a message appear after clearing the history.

            // else {
            //     this.completions.push( completion );
            // }
        },
        addUserMessageToConversationHistory( user_prompt, attached_files = [] ) {
            let message = user_prompt;
            if ( Array.isArray( attached_files ) && attached_files.length > 0 ) {
                message += '\n\n' + $.i18n._( 'Attached:' ) + ' ' + attached_files.map( ( attachment ) => attachment.filename ).join( ', ' );
            }

            let user_message = {
                role: 'user',
                result: DOMPurify.sanitize( message ).replace( /\n/g, '<br>' )
            };

            this.completions.push( user_message );
        },
        processAction( completion ) {
            if ( completion.action === 'edit_record' || completion.action === 'add_record' || completion.action === 'view_report' ) {
                LocalCacheData.setAutoFillData( completion.values );
            }

            if ( completion.redirect ) {
                Global.closeEditViews( () => {
                    if ( completion.action === 'view_report' ) {
                        IndexViewController.openReport( LocalCacheData.current_open_primary_controller, completion.section );
                    } else {
                        //Global.setURLToBrowser fails if already on the list view, therefore we just open the edit view directly.
                        if ( LocalCacheData.current_open_primary_controller.viewId === completion.section ) {
                            if ( completion.action === 'add_record' ) {
                                LocalCacheData.current_open_primary_controller.onAddClick();
                            } else {
                                LocalCacheData.current_open_primary_controller.onEditClick( completion.record_id );
                            }
                        } else {
                            Global.setURLToBrowser( completion.redirect );
                        }
                    }
                } );
            }
        },
        addContext( user_prompt, user_input ) {
            // let last_assistant_completion = this.getLastCompletionByRole( 'assistant' );

            user_input.prompt = user_prompt;

            //Without storing messages in database, we need to pass the context back (if required) to the API with each prompt.
            user_input.response_id = this.response_id;

            return user_input;
        },
        getCompletionById( id ) {
            return this.completions.find( ( completion ) => {
                return completion.id === id;
            } );
        },
        getCompletionByMessageId( message_id ) {
            return this.completions.find( ( completion ) => {
                return completion.message_id === message_id;
            } );
        },
        getLastCompletionByRole( role ) {
            let role_completions = this.completions.filter( ( completion ) => {
                return completion.role === role;
            } );

            return role_completions[role_completions.length - 1] || null;
        },
        getPromptSettings() {
            let prompt_settings = {};
            if ( Array.isArray( this.attached_files ) && this.attached_files.length > 0 ) {
                prompt_settings.attachments = this.attached_files.map( ( attachment ) => {
                    return {
                        filename: attachment.filename,
                        mime_type: attachment.mime_type,
                        size_bytes: attachment.size_bytes,
                        file_data: attachment.file_data,
                    };
                } );
            }

            return prompt_settings;
        },
        openAttachmentPicker() {
            if ( this.input_disabled || this.is_processing_attachments ) {
                return;
            }

            this.$refs.attachment_input?.click();
        },
        onAttachmentChange( event ) {
            let selected_files = Array.from( event?.target?.files || [] );
            if ( selected_files.length === 0 ) {
                return;
            }

            if ( ( this.attached_files.length + selected_files.length ) > this.max_attachment_count ) {
                TAlertManager.showAlert( $.i18n._( 'You can attach up to 3 files per request.' ) );
                this.resetAttachmentInput();
                return;
            }

            this.is_processing_attachments = true;
            let read_promises = selected_files.map( ( file ) => this.prepareAttachmentFromFile( file ) );
            Promise.all( read_promises ).then( ( prepared_files ) => {
                prepared_files.forEach( ( prepared_file ) => {
                    if ( prepared_file ) {
                        this.attached_files.push( prepared_file );
                    }
                } );
            } ).catch( () => {
                TAlertManager.showAlert( $.i18n._( 'Unable to read one or more attached files. Please try again.' ) );
            } ).finally( () => {
                this.is_processing_attachments = false;
                this.resetAttachmentInput();
            } );
        },
        prepareAttachmentFromFile( file ) {
            return new Promise( ( resolve ) => {
                if ( !( file instanceof File ) ) {
                    resolve( null );
                    return;
                }

                if ( file.size > this.max_attachment_size_bytes ) {
                    TAlertManager.showAlert( $.i18n._( 'Each attachment must be 5MB or smaller.' ) + ' ' + file.name );
                    resolve( null );
                    return;
                }

                let reader = new FileReader();
                reader.onload = () => {
                    let data_url = typeof reader.result === 'string' ? reader.result : '';
                    if ( data_url === '' || data_url.includes( ',' ) === false ) {
                        resolve( null );
                        return;
                    }

                    //Responses API input_file supports inline base64 payloads.
                    let file_data = data_url.split( ',' )[1];
                    resolve( {
                        id: TTUUID.generateUUID(),
                        filename: file.name,
                        mime_type: file.type || 'application/octet-stream',
                        size_bytes: file.size,
                        file_data: file_data,
                    } );
                };
                reader.onerror = () => resolve( null );
                reader.readAsDataURL( file );
            } );
        },
        removeAttachment( index ) {
            if ( index < 0 || index >= this.attached_files.length ) {
                return;
            }

            this.attached_files.splice( index, 1 );
        },
        clearAttachments() {
            this.attached_files = [];
            this.resetAttachmentInput();
        },
        resetAttachmentInput() {
            if ( this.$refs.attachment_input ) {
                this.$refs.attachment_input.value = '';
            }
        },
        formatAttachmentSize( size_bytes ) {
            if ( !size_bytes || size_bytes <= 0 ) {
                return '0 B';
            }

            if ( size_bytes >= 1048576 ) {
                return ( size_bytes / 1048576 ).toFixed( 1 ) + ' MB';
            }

            if ( size_bytes >= 1024 ) {
                return Math.round( size_bytes / 1024 ) + ' KB';
            }

            return size_bytes + ' B';
        },
        formatTokenCount( tokens_used ) {
            let normalized_tokens_used = parseInt( tokens_used, 10 );
            if ( !Number.isFinite( normalized_tokens_used ) || normalized_tokens_used <= 0 ) {
                return '';
            }

            return normalized_tokens_used.toLocaleString();
        },
        onPromptInput() {
            this.autoResizeInput();
        },
        onPromptKeyDown( event ) {
            // Chat-standard behavior: Enter sends, Shift+Enter inserts newline.
            if ( event.key === 'Enter' && event.shiftKey === false ) {
                event.preventDefault();
                this.userSubmitPrompt();
            }
        },
        autoResizeInput() {
            this.$nextTick( () => {
                let input = this.$refs.user_prompt_input;
                if ( !input ) {
                    return;
                }

                input.style.height = 'auto';
                let max_height = 160;
                let next_height = Math.min( input.scrollHeight, max_height );
                input.style.height = `${next_height}px`;
                input.style.overflowY = ( input.scrollHeight > max_height ) ? 'auto' : 'hidden';
            } );
        },
        resetPromptInputHeight() {
            let input = this.$refs.user_prompt_input;
            if ( !input ) {
                return;
            }

            input.style.height = 'auto';
            input.style.overflowY = 'hidden';
        },
        userSubmitPrompt( e ) {
            e?.preventDefault();
            this.sendPrompt( this.user_prompt, this.getPromptSettings() );
        },
        cancelTTAssistant() {
            let current_message_id = this.tt_assistant_api.getMessageId();
            this.cancelled_request_generation = Math.max( this.cancelled_request_generation, this.assistant_request_generation );
            if ( Array.isArray( $.xhrPool ) ) {
                for ( let i = 0; i < $.xhrPool.length; i++ ) {
                    if ( $.xhrPool[i].url.includes( 'Class=APITTAssistant&Method=getCompletion' ) ) {
                        $.xhrPool[i].jqXHR.abort();
                    }
                }
            }

            this.show_spinner = false;
            this.input_disabled = false;

            if ( current_message_id ) {
                ProgressBar.removeProgressBar( current_message_id );
                this.replaceInProgressMessageByMessageId( current_message_id, {
                    role: 'system',
                    result: this.renderAssistantMessage( this.labels.request_cancelled ),
                    message_id: null
                } );
            }

            this.tt_assistant_api.setMessageId( null );
        },
        revokeBrowserControl() {
            Debug.Text(
                'Browser control revoked from the assistant pane.',
                'TTAssistant.vue',
                'TTAssistant',
                'revokeBrowserControl',
                10
            );

            this.is_browser_control_granted = false;
            this.cancelTTAssistant();
        },
        dispatchResizeEvent() {
            setTimeout( () => {
                window.dispatchEvent( new Event( 'resize' ) );
            }, 250 );
        },
        hideModal() {
            // Capture this before cancelTTAssistant() mutates request state. Closing while
            // a request is still running should hide/cancel the assistant, but should not
            // count as a completed conversation for the feedback prompt.
            let was_request_in_flight = this.is_request_in_flight === true;
            if ( was_request_in_flight ) {
                this.cancelTTAssistant();
            }
            this.is_visible = false;
            document.querySelector( '.layout-content' ).style.marginRight = null;
            // The helper still respects the server-side prompt_for_feedback gate inside
            // TFeedback; this close event only supplies the assistant-specific trigger.
            this.triggerFeedbackOnAssistantClose( was_request_in_flight );
            this.scheduleHiddenConversationFinalize();
            this.dispatchResizeEvent();
        },
        showModal() {
            this.clearHiddenConversationFinalizeTimer();
            if ( this.completions.length === 0 ) {
                this.createStartingMessage();
            }

            this.is_visible = true;
            document.querySelector( '.layout-content' ).style.marginRight = '480px';
            this.dispatchResizeEvent();
        },
    },
    components: {
        ProgressSpinner,
    }
};
</script>
<style scoped>
.ai-modal {
    --assistant-bubble-bg: #ffffff;
    --assistant-bubble-border: #d6e1f0;
    --assistant-bubble-text: #1f2a38;
    --user-bubble-bg: #2f6fb2;
    --user-bubble-border: #255d97;
    --user-bubble-text: #f5f9ff;
    --system-bubble-bg: #fff5da;
    --system-bubble-border: #ecd28e;
    --system-bubble-text: #4f3f15;
    background: linear-gradient( 180deg, #f6f9fd 0%, #edf2f8 100% );
    box-shadow: 5px 5px 8px 0px rgba(0, 0, 0, 0.3), 0 0 60px 5px rgba(0, 0, 0, 0.38);
    border-left: 1px solid #d5ddea;
    position: fixed;
    top: 50px;
    right: 0;
    width: 480px;
    height: calc(100vh - 50px);
    z-index: 100;
    display: flex;
    flex-direction: column;
    user-select: text;
}

.ai-modal-resize-handle {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 10px;
    cursor: ew-resize;
    z-index: 10;
}

.ai-modal-body {
    padding: 0.85rem 0.8rem 0.5rem;
    flex-grow: 1;
    overflow-y: auto; /* Adds scrolling if the content is too long */
}

.ai-modal-body.has-inflight-scroll-slack {
    padding-bottom: clamp(420px, 82vh, 760px);
}

.ai-modal-footer {
    padding: 0.65rem 0.75rem 0.85rem;
    height: auto;
    border-top: 1px solid #d7e1ef;
    background: rgba(248, 251, 255, 0.96);
    box-shadow: 0 -4px 10px rgba(32, 56, 86, 0.05);
}

.ai-input {
    width: 100%;
}

.input-wrapper {
    position: relative;
    display: flex;
    width: 100%;
}

.attachment-input {
    display: none;
}

.ai-textarea {
    min-height: 77px; /* Keep at least two visible lines for chat-style composing, with a slightly taller default input area. */
    max-height: 160px;
    resize: none;
    border: 1px solid #b8cce3;
    border-radius: 12px;
    background: #ffffff;
    color: #24364a;
    padding: 0.58rem 0.72rem 2.95rem;
    font-size: 0.95rem;
    line-height: 1.4;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.ai-textarea:focus {
    outline: none;
    border-color: #4a7cb3;
    box-shadow: 0 0 0 3px rgba(74, 124, 179, 0.14);
}

.ai-textarea:disabled {
    background: #f1f5fb;
    color: #7a8ea8;
    cursor: not-allowed;
}

.composer-actions {
    position: absolute;
    left: 0.46rem;
    right: 0.46rem;
    bottom: 0.42rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    pointer-events: none;
}

.composer-left-actions {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    pointer-events: auto;
}

.composer-icon-button {
    width: 34px;
    height: 34px;
    border-radius: 999px;
    border: 1px solid transparent;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    pointer-events: auto;
    transition: background-color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
}

.composer-icon-button .tticon {
    font-size: 1.08rem !important;
}

.composer-attachment-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-top: 0;
    width: 100%;
}

.composer-attachment-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    max-width: 100%;
    background: #ebf2fb;
    border: 1px solid #c6d7ea;
    color: #294867;
    border-radius: 8px;
    padding: 0.14rem 0.45rem 0.14rem 0.5rem;
    font-size: 0.74rem;
}

.composer-attachment-name {
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.composer-attachment-size {
    color: #4a6683;
}

.composer-attachment-remove {
    border: none;
    background: transparent;
    color: #30547a;
    border-radius: 999px;
    width: 1rem;
    height: 1rem;
    line-height: 1rem;
    font-size: 0.85rem;
    padding: 0;
    cursor: pointer;
}

.composer-attachment-remove:hover {
    background: #d5e4f3;
}

.ai-modal-close {
    font-size: 2rem;
    position: absolute;
    right: 1rem;
    cursor: pointer
}

.ai-modal-title {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.42rem;
    font-weight: 800;
    font-size: 1.44rem;
    line-height: 1.2;
    margin: 0.8rem 2.5rem 0.6rem;
    color: #223247;
}

/* Match icon and title text size for visual balance in the assistant header. */
.ai-modal-title > span {
    font-size: inherit;
    line-height: inherit;
}

/* Keep the header icon static and visible on the light assistant panel. */
.ai-title-icon {
    font-size: 1.44rem !important;
    color: #2f6fb2;
    background: transparent;
    border: none;
    width: auto;
    height: auto;
    flex-shrink: 0;
}

.conversation-history {
    display: flex;
    width: 100%;
    margin: 0 0 0.65rem;
}

.conversation-message {
    position: relative;
    max-width: 88%;
    padding: 0.65rem 0.85rem;
    border: 1px solid transparent;
    border-radius: 16px;
    line-height: 1.45;
    font-size: 0.95rem;
    overflow-wrap: break-word;
    box-shadow: 0 1px 1px rgba(20, 34, 53, 0.06);
}

.from-assistant {
    justify-content: flex-start;
}

.from-user {
    justify-content: flex-end;
}

.from-system {
    justify-content: center;
}

.assistant-message,
.choice-message {
    background: var(--assistant-bubble-bg);
    color: var(--assistant-bubble-text);
    border-color: var(--assistant-bubble-border);
    border-bottom-left-radius: 7px;
}

.assistant-message {
    display: inline-flex;
    align-items: flex-start;
    gap: 0.45rem;
}

.assistant-inline-spinner-slot {
    flex: 0 0 auto;
    width: 16px;
    min-width: 16px;
    display: inline-flex;
    justify-content: center;
    margin-top: 0.16rem;
}

.assistant-inline-spinner {
    flex: 0 0 auto;
}

.assistant-inline-message-content {
    display: block;
    flex: 1 1 auto;
    min-width: 0;
}

.assistant-inline-message-body {
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
    flex-direction: column;
}

.assistant-reasoning-summary {
    display: block;
    margin-bottom: 0.45rem;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.55);
    border: 1px solid rgba(69, 96, 127, 0.16);
    color: #45607f;
    font-size: 0.92rem;
    line-height: 1.45;
}

.assistant-reasoning-summary-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.5rem 0.65rem;
}

.assistant-reasoning-summary-toggle {
    border: none;
    background: transparent;
    color: inherit;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    cursor: pointer;
    font: inherit;
    flex: 0 0 auto;
}

.assistant-reasoning-summary-preview {
    flex: 1 1 auto;
    min-width: 0;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.assistant-reasoning-summary-toggle .pi {
    font-size: 0.8rem;
    flex: 0 0 auto;
}

.assistant-reasoning-summary-content {
    padding: 0 0.65rem 0.5rem;
}

.assistant-reasoning-summary.is-collapsed {
    margin-bottom: 0.35rem;
}

.assistant-feedback-controls {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    margin-top: 0.35rem;
}

.assistant-feedback-button {
    border: none;
    background: transparent;
    color: #45607f;
    border-radius: 0;
    width: auto;
    height: auto;
    padding: 0.08rem 0.12rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: color 0.15s ease;
    cursor: pointer;
}

.assistant-feedback-button .tticon {
    font-size: 1.05rem !important;
}

.assistant-feedback-button:hover:not(:disabled) {
    background: transparent;
    color: #2f6fb2;
}

.assistant-feedback-button.is-selected {
    color: #1f5f9f;
}

.assistant-feedback-button:disabled {
    opacity: 0.65;
    cursor: wait;
}

/* Override the component-wide spinner SVG sizing so inline chat spinner stays compact. */
::v-deep(.assistant-inline-spinner .p-progress-spinner-svg) {
    width: 16px !important;
    height: 16px !important;
}

.assistant-message::after,
.choice-message::after {
    content: '';
    position: absolute;
    left: -6px;
    bottom: 9px;
    width: 0;
    height: 0;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-right: 7px solid var(--assistant-bubble-bg);
}

.assistant-message::before,
.choice-message::before {
    content: '';
    position: absolute;
    left: -8px;
    bottom: 8px;
    width: 0;
    height: 0;
    border-top: 7px solid transparent;
    border-bottom: 7px solid transparent;
    border-right: 8px solid var(--assistant-bubble-border);
}

.user-message {
    background: var(--user-bubble-bg);
    color: var(--user-bubble-text);
    border-color: var(--user-bubble-border);
    border-bottom-right-radius: 7px;
}

.user-message::after {
    content: '';
    position: absolute;
    right: -6px;
    bottom: 9px;
    width: 0;
    height: 0;
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    border-left: 7px solid var(--user-bubble-bg);
}

.user-message::before {
    content: '';
    position: absolute;
    right: -8px;
    bottom: 8px;
    width: 0;
    height: 0;
    border-top: 7px solid transparent;
    border-bottom: 7px solid transparent;
    border-left: 8px solid var(--user-bubble-border);
}

.system-message {
    background: var(--system-bubble-bg);
    color: var(--system-bubble-text);
    border-color: var(--system-bubble-border);
    max-width: 94%;
}

::v-deep(.conversation-message p) {
    margin: 0 0 0.55rem;
}

::v-deep(.conversation-message p:last-child) {
    margin-bottom: 0;
}

::v-deep(.conversation-message ul),
::v-deep(.conversation-message ol) {
    margin: 0.2rem 0 0.5rem;
    padding-left: 1.2rem;
}

::v-deep(.assistant-message a),
::v-deep(.choice-message a),
::v-deep(.system-message a) {
    color: #1a64ad;
}

::v-deep(.user-message a) {
    color: #e0efff;
}

/* Render generated-file links as compact attachment chips for clearer download UX. */
::v-deep(.assistant-message .assistant-download-link) {
    display: inline-flex;
    align-items: center;
    gap: 0.36rem;
    margin: 0.3rem 0 0.1rem;
    padding: 0.34rem 0.52rem;
    border: 1px solid #bfd4ee;
    border-radius: 11px;
    background: #edf5ff;
    color: #1c5fa4;
    text-decoration: none;
    font-size: 0.88rem;
    line-height: 1.2;
}

::v-deep(.assistant-message .assistant-download-link:hover) {
    background: #e0eeff;
    border-color: #a8c6ea;
    color: #174f88;
}

::v-deep(.assistant-message .assistant-download-icon) {
    font-size: 1rem !important;
}

::v-deep(.assistant-message .assistant-download-name) {
    font-weight: 600;
    max-width: 210px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

::v-deep(.assistant-message .assistant-download-action) {
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    background: #d7e8fb;
    color: #1a5fa6;
    border-radius: 8px;
    padding: 0.12rem 0.34rem;
}

/* Cap heading sizes only in assistant-rendered HTML so user/system bubbles keep default styles. */
::v-deep(.assistant-message h1),
::v-deep(.assistant-message h2),
::v-deep(.assistant-message h3),
::v-deep(.assistant-message h4),
::v-deep(.assistant-message h5),
::v-deep(.assistant-message h6) {
    line-height: 1.3;
    margin: 0.5rem 0;
}

::v-deep(.assistant-message h1) {
    font-size: 1.35rem;
}

::v-deep(.assistant-message h2) {
    font-size: 1.2rem;
}

::v-deep(.assistant-message h3) {
    font-size: 1.1rem;
}

::v-deep(.assistant-message h4),
::v-deep(.assistant-message h5),
::v-deep(.assistant-message h6) {
    font-size: 1rem;
}

.clear-button {
    background: transparent;
    border: none;
    border-radius: 0;
    box-shadow: none;
    color: #2a4f78;
    width: auto;
    height: auto;
    padding: 0.08rem 0.16rem;
}

.attachment-button {
    background: transparent;
    border: none;
    border-radius: 0;
    box-shadow: none;
    color: #2a4f78;
    width: auto;
    height: auto;
    padding: 0.08rem 0.16rem;
}

.attachment-button .tticon,
.clear-button .tticon {
    font-size: 2.16rem !important; /* Approximately 2x the base composer icon size. */
}

.attachment-button:hover:not(:disabled) {
    background: transparent;
    color: #1e456e;
}

.attachment-button:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

.clear-button:hover {
    background: transparent;
    color: #1e456e;
}

.send-button {
    background: #2f6fb2;
    border-color: #245a91;
    color: #ffffff;
    box-shadow: 0 1px 2px rgba(18, 58, 100, 0.22);
}

.send-button .tticon {
    color: #ffffff;
}

.send-button:hover:not(:disabled) {
    background: #265f9a;
    border-color: #1e4f81;
}

.send-button:disabled {
    background: #96afc8;
    border-color: #88a3bf;
    cursor: not-allowed;
    opacity: 0.85;
}

.stop-button {
    background: #5f86b3;
    border-color: #4a709d;
    color: #ffffff;
    box-shadow: 0 1px 2px rgba(18, 58, 100, 0.18);
}

.stop-button .tticon {
    color: #ffffff;
    font-size: 1.28rem !important;
}

.stop-button:hover:not(:disabled) {
    background: #4f78a7;
    border-color: #3f648d;
}

.form-container.ai-form {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0.45rem;
    width: 100%;
}

.browser-control-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    border: 1px solid #c65b5b;
    background: #fbe3e3;
    color: #7a2020;
    border-radius: 0.55rem;
    padding: 0.55rem 0.75rem;
}

.browser-control-banner-text {
    font-size: 0.92rem;
    font-weight: 600;
}

.browser-control-revoke-button {
    appearance: none;
    border: 1px solid #b44848;
    background: #ffffff;
    color: #8c2323;
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    padding: 0.28rem 0.7rem;
    cursor: pointer;
}

.browser-control-revoke-button:hover {
    background: #fff3f3;
    border-color: #9d3a3a;
}

::v-deep(.p-progress-spinner-circle) {
    animation: p-progress-spinner-dash 1.5s ease-in-out infinite, custom-progress-spinner-color 6s ease-in-out infinite;
}

@keyframes custom-progress-spinner-color {
    100%,
    0% {
        stroke: #000000;
    }
    40% {
        stroke: #000000;
    }
    66% {
        stroke: #000000;
    }
    80%,
    90% {
        stroke: #000000;
    }
}

::v-deep(.p-progress-spinner-svg) {
    width: 25px;
    height: 25px;
}

::v-deep(.p-progress-spinner) {
    position: relative;
    left: 0;
}

.ai-choice-list {
    list-style-type: none;
    padding: 0;
    margin: 0.4rem 0 0;
}

.assistant-quick-reply-list {
    margin-top: 0.55rem;
}

.assistant-quick-reply-label {
    margin-top: 0.55rem;
    font-size: 0.83rem;
    font-weight: 600;
    color: #45607f;
}

.ai-choice {
    padding: 0.5rem;
    margin-top: 0.45rem;
    cursor: pointer;
    background: #5382b7;
    border-radius: 9px;
    color: #fff;
    font-size: 0.9rem;
    transition: background-color 0.15s ease, transform 0.15s ease;
}

.ai-choice:hover {
    background-color: #426d9d;
    transform: translateY( -1px );
}

.ai-disclaimer {
    position: relative;
    margin-bottom: 0;
    padding: 0.42rem 0.75rem 0.5rem;
    background: #ffffff;
    border-top: 1px solid #e3e9f2;
}

.ai-disclaimer-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.55rem;
    flex-wrap: wrap;
    text-align: center;
}

.ai-token-counter {
    color: #6f8298;
    font-size: 0.72rem;
    letter-spacing: 0.01em;
    white-space: nowrap;
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY( -50% );
}

@media ( max-width: 520px ) {
    .ai-token-counter {
        position: static;
        display: block;
        margin-top: 0.2rem;
        transform: none;
    }
}

::v-deep(.annotated-text) {
    position: relative;
    display: inline-block;
    color: #007bff;
    cursor: pointer;
}

::v-deep(.annotation-content) {
    display: none;
}

.annotation-overlay {
    position: fixed;
    z-index: 1000;
    background-color: #fff;
    border: 1px solid #000;
    border-radius: 2px;
    padding: 7px;
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.3s;
    pointer-events: none;
    width: 500px;
    height: 300px;
    overflow-y: auto;
}

@media ( max-width: 900px ) {
    .ai-modal {
        width: 100vw !important;
        max-width: 100vw;
    }

    .ai-modal-resize-handle {
        display: none;
    }

    .conversation-message {
        max-width: 94%;
    }
}

</style>
